import { useState, useEffect } from 'react'
import { useAuth } from '../context/auth'
import { useNavigate } from 'react-router-dom'
import { supabase, Part, Profile, BusinessUser } from '../lib/supabase'
import { Modal, TeamMemberSuccessModal, ErrorModal, ConfirmModal } from '../components/Modal'
import { uploadImage } from '../lib/cloudinary'
import { hasRole, getAllRoles } from '../lib/roleUtils'
import { UserRole } from '../types'
import {
  LayoutDashboard, Package, Settings, Store, Plus, Boxes, MessageSquare,
  DollarSign, CreditCard, CheckCircle, Clock, TrendingUp, BarChart3,
  Activity, LogOut, Users, FileText, Star, Bell, Search,
  Filter, MoreVertical, Edit, Trash2, ArrowRight, Menu, X, User, ChevronDown, BarChart, AlertCircle, Building2, Wrench, Save, X as XIcon, XCircle, Mail, BadgeCheck, Truck
} from 'lucide-react'

type Tab = 'overview' | 'inventory' | 'orders' | 'history' | 'earnings' | 'settings' | 'reports' | 'team'

export default function PartsVendorDashboard() {
  const { profile, signOut } = useAuth()
  const navigate = useNavigate()
  const [tab, setTab] = useState<Tab>('overview')
  const [parts, setParts] = useState<Part[]>([])
  const [orders, setOrders] = useState<any[]>([])
  const [loadingOrders, setLoadingOrders] = useState(false)
  const [ordersPage, setOrdersPage] = useState(1)
  const [ordersItemsPerPage] = useState(10)
  const [historyOrders, setHistoryOrders] = useState<any[]>([])
  const [loadingHistory, setLoadingHistory] = useState(false)
  const [historySearch, setHistorySearch] = useState('')
  const [historyPage, setHistoryPage] = useState(1)
  const [historyItemsPerPage] = useState(10)
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [showUserMenu, setShowUserMenu] = useState(false)
  const [showSwitchRoleModal, setShowSwitchRoleModal] = useState(false)

  const userRoles = getAllRoles(profile)

  const availableRoles: { value: UserRole; label: string; icon: any; description: string }[] = [
    { value: 'tower', label: 'Tower Services', icon: Truck, description: 'Provide towing and breakdown services' },
    { value: 'garage', label: 'Garage Services', icon: Wrench, description: 'Offer vehicle repair and maintenance services' },
    { value: 'insurer', label: 'Insurance Provider', icon: Building2, description: 'Provide vehicle insurance services' },
  ]
  const [settings, setSettings] = useState({
    business_name: '',
    business_registration: '',
    tax_pin: '',
    business_phone: '',
    business_email: '',
    business_address: '',
    bank_name: '',
    bank_account_number: '',
    mpesa_number: ''
  })
  const [saving, setSaving] = useState(false)
  const [saveSuccess, setSaveSuccess] = useState(false)
  const [showAddPartModal, setShowAddPartModal] = useState(false)
  const [categories, setCategories] = useState<{id: string, name: string}[]>([])
  const [loadingCategories, setLoadingCategories] = useState(true)
  const [showAddCategoryModal, setShowAddCategoryModal] = useState(false)
  const [newCategoryName, setNewCategoryName] = useState('')
  const [selectedImage, setSelectedImage] = useState<File | null>(null)
  const [imagePreview, setImagePreview] = useState<string | null>(null)
  const [uploadingImage, setUploadingImage] = useState(false)
  const [newPart, setNewPart] = useState({
    name: '',
    category: '',
    price: '',
    stock: '',
    condition: 'new',
    description: '',
    car_make: '',
    car_model: '',
    year_range: ''
  })
  const [showEditBusinessModal, setShowEditBusinessModal] = useState(false)
  const [showEditBankingModal, setShowEditBankingModal] = useState(false)
  const [showSuccessModal, setShowSuccessModal] = useState(false)
  const [successMessage, setSuccessMessage] = useState('')
  const [showErrorModal, setShowErrorModal] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')
  const [inventorySearch, setInventorySearch] = useState('')
  const [editingPart, setEditingPart] = useState<Part | null>(null)
  const [showEditPartModal, setShowEditPartModal] = useState(false)
  
  // Team management state
  const [teamMembers, setTeamMembers] = useState<any[]>([])
  const [showAddTeamMemberModal, setShowAddTeamMemberModal] = useState(false)
  const [newTeamMember, setNewTeamMember] = useState({
    email: '',
    full_name: '',
    role: 'staff' as 'admin' | 'staff'
  })
  const [addingTeamMember, setAddingTeamMember] = useState(false)
  const [showTeamSuccessModal, setShowTeamSuccessModal] = useState(false)
  const [teamSuccessModalData, setTeamSuccessModalData] = useState({
    email: '',
    fullName: '',
    tempPassword: '',
    role: '',
    businessName: '',
    emailSent: false
  })
  const [showTeamErrorModal, setShowTeamErrorModal] = useState(false)
  const [teamErrorModalData, setTeamErrorModalData] = useState({
    title: '',
    message: ''
  })
  const [showConfirmModal, setShowConfirmModal] = useState(false)
  const [confirmModalData, setConfirmModalData] = useState({
    title: '',
    message: '',
    onConfirm: () => {}
  })

  useEffect(() => {
    if (!profile) return
    supabase.from('parts').select('*').eq('vendor_id', profile.id).then(({ data }) => setParts((data as Part[]) || []))
    // Initialize settings from profile
    setSettings({
      business_name: profile.business_name || '',
      business_registration: profile.business_registration || '',
      tax_pin: profile.tax_pin || '',
      business_phone: profile.business_phone || profile.phone || '',
      business_email: profile.business_email || profile.email || '',
      business_address: profile.business_address || '',
      bank_name: profile.bank_name || '',
      bank_account_number: profile.bank_account_number || '',
      mpesa_number: profile.mpesa_number || ''
    })
    // Load categories
    loadCategories()

    // Load team members
    loadTeamMembers()
  }, [profile])

  // Load orders when tab changes to orders
  useEffect(() => {
    if (tab === 'orders' && profile?.verification_status === 'verified') {
      loadOrders()
    }
  }, [tab, profile, ordersPage])

  // Load history when tab changes to history
  useEffect(() => {
    if (tab === 'history' && profile?.verification_status === 'verified') {
      loadHistory()
    }
  }, [tab, profile, historyPage])

  const loadOrders = async () => {
    if (!profile) return
    setLoadingOrders(true)
    try {
      console.log('=== Loading orders for vendor ===')
      console.log('Vendor ID:', profile.id)

      // First, try to fetch from part_order_vendor_assignments (all non-delivered)
      const { data: assignments, error: assignmentsError } = await supabase
        .from('part_order_vendor_assignments')
        .select('*')
        .eq('vendor_id', profile.id)
        .in('status', ['pending', 'confirmed', 'processing', 'ready'])

      console.log('Assignments query result:', assignments, assignmentsError)

      if (assignmentsError) {
        console.error('Error loading assignments:', assignmentsError)
        // Try alternative approach - fetch from part_order_items directly
        const { data: items, error: itemsError } = await supabase
          .from('part_order_items')
          .select('*')
          .eq('vendor_id', profile.id)

        console.log('Items query result:', items, itemsError)

        if (itemsError) {
          console.error('Error loading items:', itemsError)
          setOrders([])
        } else if (items && items.length > 0) {
          // Group items by order_id
          const orderGroups = items.reduce((acc: any, item: any) => {
            if (!acc[item.order_id]) {
              acc[item.order_id] = []
            }
            acc[item.order_id].push(item)
            return acc
          }, {})

          // Fetch order details for each order
          const orderIds = Object.keys(orderGroups)
          const { data: orders } = await supabase
            .from('part_orders')
            .select('*')
            .in('id', orderIds)

          console.log('Orders result:', orders)

          const ordersWithItems = (orders || []).map((order: any) => ({
            ...order,
            items: orderGroups[order.id] || [],
            status: order.status || 'pending',
            total_amount: orderGroups[order.id]?.reduce((sum: number, item: any) => sum + item.total_price, 0) || 0
          }))

          console.log('Orders with items:', ordersWithItems)
          setOrders(ordersWithItems)
        } else {
          setOrders([])
        }
      } else if (assignments && assignments.length > 0) {
        // Fetch order details for each assignment
        const orderIds = assignments.map((a: any) => a.order_id)
        const { data: orders } = await supabase
          .from('part_orders')
          .select('*')
          .in('id', orderIds)

        console.log('Orders from assignments:', orders)

        // Fetch items for each order
        const ordersWithItems = await Promise.all(
          (assignments || []).map(async (assignment: any) => {
            const { data: items } = await supabase
              .from('part_order_items')
              .select('*')
              .eq('order_id', assignment.order_id)
              .eq('vendor_id', profile.id)

            return {
              ...assignment,
              order: orders?.find((o: any) => o.id === assignment.order_id),
              items: items || []
            }
          })
        )

        console.log('Orders with items:', ordersWithItems)
        setOrders(ordersWithItems)
      } else {
        console.log('No assignments found')
        setOrders([])
      }
    } catch (error) {
      console.error('Error loading orders:', error)
      setOrders([])
    } finally {
      setLoadingOrders(false)
    }
  }

  const [showOrderStatusConfirm, setShowOrderStatusConfirm] = useState(false)
  const [orderStatusConfirmData, setOrderStatusConfirmData] = useState<{
    assignmentId: string
    orderId: string
    currentStatus: string
    newStatus: string
    orderRef: string
  } | null>(null)
  const [showOrderStatusSuccess, setShowOrderStatusSuccess] = useState(false)
  const [orderStatusSuccessData, setOrderStatusSuccessData] = useState<{
    newStatus: string
    orderRef: string
  } | null>(null)

  const updateOrderStatus = async (assignmentId: string, newStatus: string, orderId?: string) => {
    // Update vendor assignment status
    const { error: assignmentError } = await supabase
      .from('part_order_vendor_assignments')
      .update({ status: newStatus })
      .eq('id', assignmentId)

    // Update items for this order to match status
    if (orderId) {
      const { error: itemsError } = await supabase
        .from('part_order_items')
        .update({ item_status: newStatus })
        .eq('order_id', orderId)

      if (assignmentError || itemsError) {
        alert(`Failed to update order status: ${assignmentError?.message || itemsError?.message}`)
        return false
      }
    } else if (assignmentError) {
      alert(`Failed to update order status: ${assignmentError.message}`)
      return false
    }

    // Refresh orders
    await loadOrders()
    await loadHistory()
    return true
  }

  const handleStatusChangeRequest = (assignmentId: string, orderId: string, currentStatus: string, newStatus: string, orderRef: string) => {
    setOrderStatusConfirmData({
      assignmentId,
      orderId,
      currentStatus,
      newStatus,
      orderRef
    })
    setShowOrderStatusConfirm(true)
  }

  const confirmStatusChange = async () => {
    if (!orderStatusConfirmData) return

    const success = await updateOrderStatus(
      orderStatusConfirmData.assignmentId,
      orderStatusConfirmData.newStatus,
      orderStatusConfirmData.orderId
    )

    if (success) {
      setShowOrderStatusConfirm(false)
      setOrderStatusSuccessData({
        newStatus: orderStatusConfirmData.newStatus,
        orderRef: orderStatusConfirmData.orderRef
      })
      setShowOrderStatusSuccess(true)
    }
  }

  const loadHistory = async () => {
    if (!profile) return
    setLoadingHistory(true)
    try {
      // Fetch delivered assignments only
      const { data: assignments, error: assignmentsError } = await supabase
        .from('part_order_vendor_assignments')
        .select('*')
        .eq('vendor_id', profile.id)
        .eq('status', 'delivered')

      if (assignmentsError) {
        console.error('Error loading assignments:', assignmentsError)
        setHistoryOrders([])
      } else if (assignments && assignments.length > 0) {
        // Fetch order details for each assignment
        const orderIds = assignments.map(a => a.order_id)

        // Fetch orders WITHOUT profiles join first (to avoid RLS issues)
        const { data: orders, error: ordersError } = await supabase
          .from('part_orders')
          .select('*')
          .in('id', orderIds)
          .order('created_at', { ascending: false })

        if (ordersError) {
          console.error('Error fetching orders:', ordersError)
          setHistoryOrders([])
          return
        }

        // Fetch customer profiles separately
        const customerIds = orders?.map(o => o.customer_id) || []
        let profilesMap: any = {}
        if (customerIds.length > 0) {
          const { data: profiles } = await supabase
            .from('profiles')
            .select('id, full_name, phone, email')
            .in('id', customerIds)
          
          profilesMap = (profiles || []).reduce((acc: any, p: any) => {
            acc[p.id] = p
            return acc
          }, {})
        }

        // Merge profiles into orders
        const ordersWithProfiles = (orders || []).map((order: any) => ({
          ...order,
          profiles: profilesMap[order.customer_id] || null
        }))

        // Fetch items for each order
        const { data: items } = await supabase
          .from('part_order_items')
          .select('*')
          .in('order_id', orderIds)

        // Group items by order_id
        const orderGroups = (items || []).reduce((acc: any, item: any) => {
          if (!acc[item.order_id]) {
            acc[item.order_id] = []
          }
          acc[item.order_id].push(item)
          return acc
        }, {})

        const ordersWithItems = ordersWithProfiles.map((order: any) => {
          const assignment = assignments.find(a => a.order_id === order.id)
          return {
            ...order,
            items: orderGroups[order.id] || [],
            total_amount: orderGroups[order.id]?.reduce((sum: number, item: any) => sum + item.total_price, 0) || 0,
            completed_at: assignment?.updated_at || order.updated_at
          }
        })

        setHistoryOrders(ordersWithItems)
      } else {
        setHistoryOrders([])
      }
    } catch (error) {
      console.error('Error loading history:', error)
      setHistoryOrders([])
    } finally {
      setLoadingHistory(false)
    }
  }

  const loadTeamMembers = async () => {
    if (!profile) return
    const { data } = await supabase
      .from('business_users')
      .select('*')
      .eq('business_id', profile.id)
    
    // Fetch profiles for each team member
    const userIds = (data || []).map(m => m.user_id)
    const { data: profiles } = await supabase
      .from('profiles')
      .select('id, full_name, email, phone')
      .in('id', userIds)
    
    const profilesMap = (profiles || []).reduce((acc, p) => {
      acc[p.id] = p
      return acc
    }, {} as Record<string, any>)
    
    const teamMembersWithProfiles = (data || []).map(m => ({
      ...m,
      profiles: profilesMap[m.user_id] || null
    }))
    
    setTeamMembers(teamMembersWithProfiles)
  }

  const addTeamMember = async () => {
    if (!profile) return
    setAddingTeamMember(true)
    
    try {
      // Check if user exists
      const { data: existingUser, error: userError } = await supabase
        .from('profiles')
        .select('id, full_name, email')
        .eq('email', newTeamMember.email)
        .single()

      if (userError || !existingUser) {
        setTeamErrorModalData({
          title: 'User Not Found',
          message: 'No user found with this email. Team members must already have an account in the system.'
        })
        setShowTeamErrorModal(true)
        setAddingTeamMember(false)
        return
      }

      // Check if already part of this business
      const { data: existingAssignment, error: assignmentError } = await supabase
        .from('business_users')
        .select('*')
        .eq('user_id', existingUser.id)
        .eq('business_id', profile.id)
        .single()

      if (existingAssignment) {
        setTeamErrorModalData({
          title: 'Already a Team Member',
          message: 'This user is already a member of your business.'
        })
        setShowTeamErrorModal(true)
        setAddingTeamMember(false)
        return
      }

      // Add to business_users
      const { error: businessUserError } = await supabase
        .from('business_users')
        .insert({
          user_id: existingUser.id,
          business_id: profile.id,
          role: newTeamMember.role
        })

      if (businessUserError) {
        setTeamErrorModalData({
          title: 'Team Member Creation Failed',
          message: businessUserError.message
        })
        setShowTeamErrorModal(true)
        setAddingTeamMember(false)
        return
      }

      // Refresh team members
      await loadTeamMembers()
      
      // Reset form and close modal
      setNewTeamMember({ email: '', full_name: '', role: 'staff' })
      setShowAddTeamMemberModal(false)
      
      // Show success modal
      setTeamSuccessModalData({
        email: newTeamMember.email,
        fullName: existingUser.full_name,
        tempPassword: '',
        role: newTeamMember.role,
        businessName: profile?.business_name || profile?.full_name || 'Your Business',
        emailSent: false
      })
      setShowTeamSuccessModal(true)
      
    } catch (error) {
      setTeamErrorModalData({
        title: 'Error Occurred',
        message: `An unexpected error occurred: ${(error as any).message}`
      })
      setShowTeamErrorModal(true)
    } finally {
      setAddingTeamMember(false)
    }
  }

  const removeTeamMember = async (userId: string) => {
    if (!profile) return
    
    setConfirmModalData({
      title: 'Remove Team Member',
      message: 'Are you sure you want to remove this team member? This action cannot be undone.',
      onConfirm: async () => {
        const { error } = await supabase
          .from('business_users')
          .delete()
          .eq('business_id', profile.id)
          .eq('user_id', userId)
        
        if (error) {
          setTeamErrorModalData({
            title: 'Removal Failed',
            message: `Error removing team member: ${error.message}`
          })
          setShowTeamErrorModal(true)
          return
        }
        
        await loadTeamMembers()
      }
    })
    setShowConfirmModal(true)
  }

  const resendInvitation = async (member: any) => {
    if (!profile) return

    setTeamErrorModalData({
      title: 'Feature Not Available',
      message: 'Password reset functionality coming soon. Please contact the team member directly or remove and re-add them.'
    })
    setShowTeamErrorModal(true)
  }

  const loadCategories = async () => {
    setLoadingCategories(true)
    try {
      const { data, error } = await supabase
        .from('part_categories')
        .select('id, name')
        .eq('is_active', true)
        .order('name')

      if (error) {
        console.error('Error loading categories:', error)
      } else {
        setCategories(data || [])
      }
    } catch (error) {
      console.error('Error loading categories:', error)
    } finally {
      setLoadingCategories(false)
    }
  }

  // Close user menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (showUserMenu && !(event.target as HTMLElement).closest('.user-menu-container')) {
        setShowUserMenu(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [showUserMenu])

  const totalParts = parts.length
  const lowStockParts = parts.filter(p => p.stock < 10).length

  // Filter parts based on search
  const filteredParts = parts.filter(part => {
    if (!inventorySearch) return true
    const searchLower = inventorySearch.toLowerCase()
    return (
      part.name.toLowerCase().includes(searchLower) ||
      part.category.toLowerCase().includes(searchLower) ||
      (part.car_make && part.car_make.toLowerCase().includes(searchLower)) ||
      (part.car_model && part.car_model.toLowerCase().includes(searchLower)) ||
      (part.year_range && part.year_range.toLowerCase().includes(searchLower))
    )
  })

  // Phone validation functions
  const handlePhoneKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    // Allow only numbers and + sign
    const char = e.key
    if (!/^[0-9+]$/.test(char)) {
      e.preventDefault()
    }
  }

  const handlePhoneChange = (value: string, field: string) => {
    // Remove any non-numeric characters except +
    const cleaned = value.replace(/[^0-9+]/g, '')
    setSettings({ ...settings, [field]: cleaned })
  }

  const openEditPartModal = (part: Part) => {
    setEditingPart(part)
    setNewPart({
      name: part.name,
      category: part.category,
      price: part.price.toString(),
      stock: part.stock.toString(),
      condition: part.condition || 'new',
      description: part.description || '',
      car_make: part.car_make || '',
      car_model: part.car_model || '',
      year_range: part.year_range || ''
    })
    if (part.image_url) {
      setImagePreview(part.image_url)
    }
    setShowEditPartModal(true)
  }

  const handleDeletePart = async (partId: string) => {
    if (!confirm('Are you sure you want to delete this part? This action cannot be undone.')) {
      return
    }

    try {
      const { error } = await supabase
        .from('parts')
        .delete()
        .eq('id', partId)

      if (error) {
        alert('Failed to delete part: ' + error.message)
      } else {
        // Refresh parts list
        const { data } = await supabase.from('parts').select('*').eq('vendor_id', profile?.id)
        setParts((data as Part[]) || [])
        alert('Part deleted successfully')
      }
    } catch (error) {
      alert('Failed to delete part: ' + (error instanceof Error ? error.message : 'Unknown error'))
    }
  }

  const handleSaveEditPart = async () => {
    if (!editingPart || !profile) return

    try {
      let imageUrl = editingPart.image_url

      // Upload new image if selected
      if (selectedImage) {
        setUploadingImage(true)
        try {
          imageUrl = await uploadImage(selectedImage, 'parts-inventory')
        } catch (error) {
          console.error('Image upload failed:', error)
          alert('Failed to upload image. Please try again.')
          setUploadingImage(false)
          return
        }
        setUploadingImage(false)
      }

      const { error } = await supabase
        .from('parts')
        .update({
          name: newPart.name,
          category: newPart.category,
          price: parseFloat(newPart.price),
          stock: parseInt(newPart.stock),
          condition: newPart.condition,
          description: newPart.description,
          car_make: newPart.car_make,
          car_model: newPart.car_model,
          year_range: newPart.year_range,
          image_url: imageUrl,
          updated_at: new Date().toISOString()
        })
        .eq('id', editingPart.id)

      if (error) {
        alert('Failed to update part: ' + error.message)
      } else {
        // Refresh parts list
        const { data } = await supabase.from('parts').select('*').eq('vendor_id', profile.id)
        setParts((data as Part[]) || [])
        setShowEditPartModal(false)
        setEditingPart(null)
        setSelectedImage(null)
        setImagePreview(null)
        setSuccessMessage('Part updated successfully!')
        setShowSuccessModal(true)
        setTimeout(() => setShowSuccessModal(false), 2000)
      }
    } catch (error) {
      alert('Failed to update part: ' + (error instanceof Error ? error.message : 'Unknown error'))
    }
  }

  const handleSaveSettings = async () => {
    if (!profile) return
    setSaving(true)
    try {
      const { error } = await supabase
        .from('profiles')
        .update({
          business_name: settings.business_name,
          business_registration: settings.business_registration,
          tax_pin: settings.tax_pin,
          business_phone: settings.business_phone,
          business_email: settings.business_email,
          business_address: settings.business_address,
          bank_name: settings.bank_name,
          bank_account_number: settings.bank_account_number,
          mpesa_number: settings.mpesa_number,
          updated_at: new Date().toISOString()
        })
        .eq('id', profile.id)

      if (error) {
        console.error('Error saving settings:', error)
        alert('Failed to save settings')
      } else {
        setSaveSuccess(true)
        setTimeout(() => setSaveSuccess(false), 3000)
      }
    } catch (error) {
      console.error('Error saving settings:', error)
      alert('Failed to save settings')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="dashboard-layout">
      <aside className={`sidebar ${mobileMenuOpen ? 'open' : ''}`}>
        <div className="sidebar-brand">
          <span>Mk</span>ononi
          <button
            className="mobile-close-btn"
            onClick={() => setMobileMenuOpen(false)}
            style={{
              position: 'absolute',
              right: '1rem',
              top: '1.5rem',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              color: 'var(--text)',
              padding: '0.5rem'
            }}
          >
            <X size={20} />
          </button>
        </div>
        <nav className="sidebar-nav">
          <div className="sidebar-section">
            <div className="sidebar-section-title">Overview</div>
            <div className={`sidebar-item ${tab === 'overview' ? 'active' : ''}`} onClick={() => setTab('overview')}>
              <LayoutDashboard size={20} />
              <span>Dashboard</span>
            </div>
          </div>
          <div className="sidebar-section">
            <div className="sidebar-section-title">Operations</div>
            <div 
              className={`sidebar-item ${tab === 'inventory' ? 'active' : ''}`}
              onClick={() => profile?.verification_status === 'verified' ? setTab('inventory') : null}
              style={{ 
                cursor: profile?.verification_status === 'verified' ? 'pointer' : 'not-allowed',
                opacity: profile?.verification_status === 'verified' ? 1 : 0.5
              }}
            >
              <Boxes size={20} />
              <span>Inventory</span>
              {totalParts > 0 && <span className="badge">{totalParts}</span>}
            </div>
            <div
              className={`sidebar-item ${tab === 'orders' ? 'active' : ''}`}
              onClick={() => profile?.verification_status === 'verified' ? setTab('orders') : null}
              style={{
                cursor: profile?.verification_status === 'verified' ? 'pointer' : 'not-allowed',
                opacity: profile?.verification_status === 'verified' ? 1 : 0.5
              }}
            >
              <Package size={20} />
              <span>Orders</span>
            </div>
            <div
              className={`sidebar-item ${tab === 'history' ? 'active' : ''}`}
              onClick={() => profile?.verification_status === 'verified' ? setTab('history') : null}
              style={{
                cursor: profile?.verification_status === 'verified' ? 'pointer' : 'not-allowed',
                opacity: profile?.verification_status === 'verified' ? 1 : 0.5
              }}
            >
              <Clock size={20} />
              <span>History</span>
            </div>
          </div>
          <div className="sidebar-section">
            <div className="sidebar-section-title">Financial</div>
            <div 
              className={`sidebar-item ${tab === 'earnings' ? 'active' : ''}`}
              onClick={() => profile?.verification_status === 'verified' ? setTab('earnings') : null}
              style={{ 
                cursor: profile?.verification_status === 'verified' ? 'pointer' : 'not-allowed',
                opacity: profile?.verification_status === 'verified' ? 1 : 0.5
              }}
            >
              <DollarSign size={20} />
              <span>Earnings</span>
            </div>
            <div 
              className="sidebar-item"
              onClick={() => profile?.verification_status === 'verified' ? navigate('/payments') : null}
              style={{ 
                cursor: profile?.verification_status === 'verified' ? 'pointer' : 'not-allowed',
                opacity: profile?.verification_status === 'verified' ? 1 : 0.5
              }}
            >
              <CreditCard size={20} />
              <span>Payments</span>
            </div>
          </div>
          <div className="sidebar-section">
            <div className="sidebar-section-title">Reports</div>
            <div 
              className={`sidebar-item ${tab === 'reports' ? 'active' : ''}`}
              onClick={() => profile?.verification_status === 'verified' ? setTab('reports') : null}
              style={{ 
                cursor: profile?.verification_status === 'verified' ? 'pointer' : 'not-allowed',
                opacity: profile?.verification_status === 'verified' ? 1 : 0.5
              }}
            >
              <BarChart size={20} />
              <span>Reports</span>
            </div>
          </div>
          <div className="sidebar-section">
            <div className="sidebar-section-title">Account</div>
            <div className={`sidebar-item ${tab === 'team' ? 'active' : ''}`} onClick={() => profile?.verification_status === 'verified' ? setTab('team') : null}
              style={{
                cursor: profile?.verification_status === 'verified' ? 'pointer' : 'not-allowed',
                opacity: profile?.verification_status === 'verified' ? 1 : 0.5
              }}
            >
              <Users size={20} />
              <span>Team</span>
            </div>
            <div className={`sidebar-item ${tab === 'settings' ? 'active' : ''}`} onClick={() => setTab('settings')}>
              <Settings size={20} />
              <span>Settings</span>
            </div>
          </div>
          <div className="sidebar-footer">
            <div className="name">{profile?.full_name}</div>
            <div className="role">{profile?.role}</div>
            <button className="sign-out" onClick={signOut}>
              <LogOut size={16} />
              <span>Sign Out</span>
            </button>
          </div>
        </nav>
      </aside>

      {mobileMenuOpen && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            zIndex: 99
          }}
          className="mobile-overlay"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      <main className="dashboard-main">
        <header className="dashboard-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flex: 1, minWidth: 0 }}>
            <button
              className="mobile-menu-btn"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: '0.5rem',
                color: 'var(--text)',
                flexShrink: 0
              }}
            >
              <Menu size={24} />
            </button>
            <div style={{ minWidth: 0, flex: 1 }}>
              <h1 className="dashboard-title">Parts Shop Portal</h1>
              <p className="dashboard-subtitle">Welcome back, {profile?.full_name}</p>
            </div>
          </div>
          <div className="dashboard-header-actions" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexShrink: 0 }}>
            {/* Switch Role Button - Only show if multi-role */}
            {userRoles.length > 1 && (
              <button
                onClick={() => setShowSwitchRoleModal(true)}
                className="btn btn-outline"
                style={{ padding: '0.5rem 1rem', gap: '0.5rem' }}
              >
                <BadgeCheck size={18} />
                <span className="desktop-only" style={{ marginLeft: '0.5rem' }}>Switch Role</span>
              </button>
            )}

            {/* Account Status Banner - Hidden on mobile */}
            {profile?.verification_status === 'pending' && (
              <div className="desktop-only" style={{
                background: 'var(--amber-light)',
                border: '1px solid var(--amber)',
                borderRadius: '8px',
                padding: '0.5rem 0.75rem',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}>
                <AlertCircle size={16} color="var(--amber)" />
                <div>
                  <div style={{ fontWeight: 600, fontSize: '0.85rem', color: 'var(--amber)' }}>
                    Pending Approval
                  </div>
                </div>
              </div>
            )}

            <div className="user-menu-container" style={{ position: 'relative' }}>
              <button
                onClick={() => setShowUserMenu(!showUserMenu)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  padding: '0.5rem 0.75rem',
                  background: 'white',
                  border: '1px solid var(--border)',
                  borderRadius: 'var(--radius-sm)',
                  cursor: 'pointer',
                  fontWeight: 600,
                  color: 'var(--text)',
                  fontSize: '0.8rem'
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = 'var(--bg)'}
                onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
              >
                <User size={16} />
                <span className="desktop-only">{profile?.full_name?.split(' ')[0] || 'Vendor'}</span>
                <ChevronDown size={14} />
              </button>
              {showUserMenu && (
                <div
                  style={{
                    position: 'absolute',
                    top: '100%',
                    right: 0,
                    marginTop: '0.5rem',
                    background: 'white',
                    border: '1px solid var(--border)',
                    borderRadius: 'var(--radius-sm)',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                    minWidth: '160px',
                    zIndex: 1000
                  }}
                >
                  <button
                    onClick={() => { setTab('settings'); setShowUserMenu(false) }}
                    style={{
                      width: '100%',
                      padding: '0.75rem 1rem',
                      border: 'none',
                      background: 'none',
                      cursor: 'pointer',
                      textAlign: 'left',
                      fontSize: '0.875rem',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem'
                    }}
                  >
                    <Settings size={16} />
                    Account Settings
                  </button>
                  <button
                    onClick={signOut}
                    style={{
                      width: '100%',
                      padding: '0.75rem 1rem',
                      border: 'none',
                      background: 'none',
                      cursor: 'pointer',
                      textAlign: 'left',
                      fontSize: '0.875rem',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      color: '#dc2626'
                    }}
                  >
                    <LogOut size={16} />
                    Sign Out
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>

        {tab === 'overview' && (
          <div className="dashboard-content">
            <div className="stats-grid">
              <div className="stat-card">
                <div className="stat-icon" style={{ background: '#fee2e2', color: '#dc2626' }}>
                  <Boxes size={24} />
                </div>
                <div className="stat-content">
                  <div className="stat-label">Total Parts</div>
                  <div className="stat-value">{totalParts}</div>
                  <div className="stat-trend">In inventory</div>
                </div>
              </div>
              <div className="stat-card">
                <div className="stat-icon" style={{ background: '#fef3c7', color: '#d97706' }}>
                  <Package size={24} />
                </div>
                <div className="stat-content">
                  <div className="stat-label">Low Stock</div>
                  <div className="stat-value">{lowStockParts}</div>
                  <div className="stat-trend">Need restock</div>
                </div>
              </div>
              <div className="stat-card">
                <div className="stat-icon" style={{ background: '#d1fae5', color: '#059669' }}>
                  <DollarSign size={24} />
                </div>
                <div className="stat-content">
                  <div className="stat-label">Total Earnings</div>
                  <div className="stat-value">KES 0</div>
                  <div className="stat-trend">All time revenue</div>
                </div>
              </div>
            </div>

            {profile?.verification_status === 'verified' ? (
              <>
                <div className="card">
                  <div className="card-header">
                    <h2>Quick Actions</h2>
                  </div>
                  <div className="quick-actions">
                    <button className="action-card" onClick={() => setShowAddPartModal(true)}>
                      <Plus size={24} style={{ color: '#dc2626' }} />
                      <span>Add Part</span>
                    </button>
                    <button className="action-card" onClick={() => setTab('orders')}>
                      <Package size={24} style={{ color: '#dc2626' }} />
                      <span>View Orders</span>
                    </button>
                    <button className="action-card" onClick={() => setTab('earnings')}>
                      <BarChart3 size={24} style={{ color: '#dc2626' }} />
                      <span>Sales Report</span>
                    </button>
                    <button className="action-card" onClick={() => setTab('settings')}>
                      <Settings size={24} style={{ color: '#dc2626' }} />
                      <span>Settings</span>
                    </button>
                  </div>
                </div>

                <div className="card">
                  <div className="card-header">
                    <h2>Recent Parts</h2>
                    <button
                      onClick={() => setTab('inventory')}
                      style={{
                        padding: '0.5rem 1rem',
                        background: 'var(--red)',
                        color: 'white',
                        border: 'none',
                        borderRadius: 'var(--radius-sm)',
                        cursor: 'pointer',
                        fontWeight: 600,
                        fontSize: '0.875rem',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem'
                      }}
                    >
                      View All
                      <ArrowRight size={16} />
                    </button>
                  </div>
                  {parts.length === 0 ? (
                    <div className="empty-state" style={{
                      textAlign: 'center',
                      padding: '3rem 2rem',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: '1rem'
                    }}>
                      <Package size={64} style={{ color: '#d1d5db', marginBottom: '0.5rem' }} />
                      <p style={{ fontSize: '1rem', color: '#6b7280', fontWeight: 500 }}>No parts in inventory yet</p>
                      <button
                        className="btn-primary"
                        onClick={() => setShowAddPartModal(true)}
                        style={{
                          padding: '0.75rem 1.5rem',
                          background: 'var(--red)',
                          color: 'white',
                          border: 'none',
                          borderRadius: 'var(--radius-sm)',
                          cursor: 'pointer',
                          fontWeight: 600,
                          fontSize: '0.875rem'
                        }}
                      >
                        Add Your First Part
                      </button>
                    </div>
                  ) : (
                    <div className="data-table-wrapper">
                      <table className="data-table">
                      <thead>
                        <tr>
                          <th>Part Name</th>
                          <th>Category</th>
                          <th>Price</th>
                          <th>Stock</th>
                          <th>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {parts.slice(0, 5).map(part => (
                          <tr key={part.id}>
                            <td>{part.name}</td>
                            <td>{part.category}</td>
                            <td>KES {part.price.toLocaleString()}</td>
                            <td>{part.stock}</td>
                            <td>
                              <button className="btn-icon" style={{ color: '#dc2626' }}>
                                <Edit size={16} />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    </div>
                  )}
                </div>
              </>
            ) : (
              <div className="card" style={{ padding: '3rem', textAlign: 'center' }}>
                <AlertCircle size={64} style={{ color: '#f59e0b', marginBottom: '1.5rem' }} />
                <h2 style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--text)', marginBottom: '1rem' }}>
                  Account Pending Approval
                </h2>
                <p style={{ fontSize: '1rem', color: 'var(--text-secondary)', marginBottom: '1.5rem', lineHeight: 1.6 }}>
                  Your account is currently under review. Once approved by our team, you will be able to manage your inventory, view orders, and access all features.
                </p>
                <div style={{ padding: '1rem', background: '#fef3c7', borderRadius: 'var(--radius-sm)', border: '1px solid #fcd34d', display: 'inline-block', textAlign: 'left' }}>
                  <div style={{ fontSize: '0.875rem', color: '#92400e', fontWeight: 600, marginBottom: '0.5rem' }}>
                    What happens next?
                  </div>
                  <ul style={{ fontSize: '0.85rem', color: '#92400e', margin: 0, paddingLeft: '1.25rem' }}>
                    <li>Our team reviews your business information</li>
                    <li>Verification typically takes 1-2 business days</li>
                    <li>You will receive an email once approved</li>
                  </ul>
                </div>
              </div>
            )}
          </div>
        )}

        {tab === 'inventory' && (
          <div className="dashboard-content">
            {profile?.verification_status === 'verified' ? (
              <div className="card">
                <div className="card-header" style={{ display: 'flex', flexDirection: 'column', gap: '1rem', alignItems: 'stretch' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem' }}>
                    <h2 style={{ margin: 0 }}>Parts Inventory</h2>
                    <button
                      className="btn-primary"
                      onClick={() => setShowAddPartModal(true)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        flexShrink: 0
                      }}
                    >
                      <Plus size={18} />
                      Add Part
                    </button>
                  </div>
                  {/* Search Bar */}
                  <div style={{
                    position: 'relative',
                    width: '100%'
                  }}>
                    <Search
                      size={18}
                      style={{
                        position: 'absolute',
                        left: '1rem',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        color: 'var(--muted)'
                      }}
                    />
                    <input
                      type="text"
                      placeholder="Search parts by name, category, make, model, or year..."
                      value={inventorySearch}
                      onChange={(e) => setInventorySearch(e.target.value)}
                      style={{
                        width: '100%',
                        padding: '0.75rem 1rem 0.75rem 3rem',
                        borderRadius: '10px',
                        border: '2px solid var(--border)',
                        fontSize: '0.9rem',
                        outline: 'none',
                        transition: 'border-color 0.2s',
                        background: 'var(--bg)'
                      }}
                      onFocus={(e) => e.currentTarget.style.borderColor = 'var(--blue)'}
                      onBlur={(e) => e.currentTarget.style.borderColor = 'var(--border)'}
                    />
                    {inventorySearch && (
                      <button
                        onClick={() => setInventorySearch('')}
                        style={{
                          position: 'absolute',
                          right: '1rem',
                          top: '50%',
                          transform: 'translateY(-50%)',
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          padding: '0.25rem',
                          color: 'var(--muted)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                      >
                        <X size={16} />
                      </button>
                    )}
                  </div>
                </div>
                {parts.length === 0 ? (
                  <div className="empty-state" style={{
                    textAlign: 'center',
                    padding: '3rem 2rem',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '1rem'
                  }}>
                    <Boxes size={64} style={{ color: '#d1d5db', marginBottom: '0.5rem' }} />
                    <p style={{ fontSize: '1rem', color: '#6b7280', fontWeight: 500 }}>No parts in inventory</p>
                    <button
                      className="btn-primary"
                      onClick={() => setShowAddPartModal(true)}
                      style={{ marginTop: '1rem' }}
                    >
                      Add Your First Part
                    </button>
                  </div>
                ) : (
                  <div className="parts-inventory-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '1rem' }}>
                    {filteredParts.length === 0 ? (
                      <div style={{
                        textAlign: 'center',
                        padding: '3rem 2rem',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: '1rem',
                        gridColumn: '1 / -1'
                      }}>
                        <Search size={64} style={{ color: '#d1d5db', marginBottom: '0.5rem' }} />
                        <p style={{ fontSize: '1rem', color: '#6b7280', fontWeight: 500 }}>No parts found matching "{inventorySearch}"</p>
                        <button
                          className="btn-outline"
                          onClick={() => setInventorySearch('')}
                          style={{ marginTop: '1rem' }}
                        >
                          Clear Search
                        </button>
                      </div>
                    ) : (
                      filteredParts.map(part => (
                        <div key={part.id} style={{
                          background: 'white',
                          borderRadius: '16px',
                          padding: '1.25rem',
                          border: '2px solid var(--border)',
                          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                          transition: 'all 0.2s',
                          position: 'relative'
                        }}>
                          <div style={{ display: 'flex', gap: '1rem', marginBottom: '0.75rem' }}>
                            {/* Part Image */}
                            <div style={{
                              width: '64px',
                              height: '64px',
                              borderRadius: '12px',
                              background: part.image_url ? `url(${part.image_url}) center/cover` : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              overflow: 'hidden',
                              flexShrink: 0
                            }}>
                              {!part.image_url && (
                                <Package size={28} color="white" style={{ opacity: 0.8 }} />
                              )}
                            </div>

                            {/* Part Name & Category */}
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ 
                                fontSize: '0.7rem', 
                                fontWeight: 600, 
                                color: 'var(--blue)', 
                                textTransform: 'uppercase', 
                                letterSpacing: '0.05em',
                                marginBottom: '0.25rem'
                              }}>
                                {part.category}
                              </div>
                              <h3 style={{ 
                                fontSize: '1rem', 
                                fontWeight: 700, 
                                color: 'var(--text)', 
                                margin: 0,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                              }}>
                                {part.name}
                              </h3>
                            </div>
                          </div>

                          {/* Vehicle Compatibility */}
                          {(part.car_make || part.car_model) && (
                            <div style={{
                              padding: '0.5rem 0.75rem',
                              background: 'var(--bg)',
                              borderRadius: '8px',
                              marginBottom: '0.75rem',
                              fontSize: '0.8rem',
                              color: 'var(--text)'
                            }}>
                              <div style={{ fontWeight: 600, marginBottom: '0.1rem' }}>
                                {part.car_make || 'N/A'} {part.car_model || ''}
                              </div>
                              <div style={{ fontSize: '0.75rem', color: 'var(--muted)' }}>
                                {part.year_range || 'All years'}
                              </div>
                            </div>
                          )}

                          {/* Price Section */}
                          <div style={{
                            padding: '0.75rem',
                            background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
                            borderRadius: '10px',
                            marginBottom: '0.75rem',
                            border: '1px solid #fcd34d'
                          }}>
                            <div style={{ fontSize: '0.7rem', fontWeight: 600, color: '#92400e', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.25rem' }}>
                              Price
                            </div>
                            <div style={{ fontSize: '1.5rem', fontWeight: 800, color: '#78350f' }}>
                              KES {part.price.toLocaleString()}
                            </div>
                          </div>

                          {/* Stock & Status */}
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
                            <div style={{ fontSize: '0.875rem', color: 'var(--text)' }}>
                              <span style={{ color: 'var(--muted)' }}>Stock:</span> {part.stock}
                            </div>
                            <span className={`badge ${part.stock < 10 ? 'badge-warning' : 'badge-success'}`} style={{ fontSize: '0.7rem', padding: '0.25rem 0.6rem' }}>
                              {part.stock < 10 ? 'Low Stock' : 'In Stock'}
                            </span>
                          </div>

                          {/* Action Buttons */}
                          <div style={{ display: 'flex', gap: '0.5rem' }}>
                            <button
                              onClick={() => openEditPartModal(part)}
                              className="btn btn-outline"
                              style={{ 
                                flex: 1,
                                padding: '0.625rem',
                                borderRadius: '8px',
                                fontSize: '0.875rem',
                                fontWeight: 600,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: '0.5rem'
                              }}
                            >
                              <Edit size={16} />
                              Edit
                            </button>
                            <button
                              onClick={() => handleDeletePart(part.id)}
                              style={{
                                padding: '0.625rem',
                                borderRadius: '8px',
                                background: 'var(--red)',
                                border: 'none',
                                color: 'white',
                                fontSize: '0.875rem',
                                fontWeight: 600,
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: '0.5rem',
                                transition: 'all 0.2s'
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.background = '#dc2626'
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.background = 'var(--red)'
                              }}
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>
            ) : (
              <div className="card" style={{ padding: '3rem', textAlign: 'center' }}>
                <AlertCircle size={64} style={{ color: '#f59e0b', marginBottom: '1.5rem' }} />
                <h2 style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--text)', marginBottom: '1rem' }}>
                  Account Pending Approval
                </h2>
                <p style={{ fontSize: '1rem', color: 'var(--text-secondary)', marginBottom: '1.5rem', lineHeight: 1.6 }}>
                  Your account is currently under review. Once approved by our team, you will be able to manage your inventory, view orders, and access all features.
                </p>
              </div>
            )}
          </div>
        )}

        {tab === 'orders' && (
          <div className="dashboard-content">
            {profile?.verification_status === 'verified' ? (
              <div className="card">
                <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem' }}>
                  <div>
                    <h2>Parts Orders</h2>
                    <p style={{ fontSize: '0.85rem', color: 'var(--muted)', marginTop: '0.25rem' }}>
                      {orders.length} pending order{orders.length !== 1 ? 's' : ''}
                    </p>
                  </div>
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <button
                      onClick={loadOrders}
                      style={{
                        padding: '0.5rem 1rem',
                        background: 'white',
                        border: '1px solid var(--border)',
                        borderRadius: 'var(--radius-sm)',
                        cursor: 'pointer',
                        fontWeight: 600,
                        fontSize: '0.85rem',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem'
                      }}
                    >
                      <Activity size={16} />
                      Refresh
                    </button>
                  </div>
                </div>
                {loadingOrders ? (
                  <div style={{ padding: '3rem', textAlign: 'center' }}>
                    <div style={{
                      width: '48px',
                      height: '48px',
                      border: '4px solid var(--border)',
                      borderTopColor: 'var(--red)',
                      borderRadius: '50%',
                      margin: '0 auto 1rem',
                      animation: 'spin 1s linear infinite'
                    }} />
                    <p>Loading orders...</p>
                  </div>
                ) : orders.length === 0 ? (
                  <div className="empty-state">
                    <Package size={48} style={{ color: '#9ca3af' }} />
                    <p>No parts orders yet</p>
                  </div>
                ) : (
                  <>
                    <div className="data-table-wrapper">
                      <table className="data-table">
                        <thead>
                          <tr>
                            <th>Reference</th>
                            <th>Customer</th>
                            <th>Items</th>
                            <th>Description</th>
                            <th>Quantity</th>
                            <th>Amount</th>
                            <th>Status</th>
                            <th>Date</th>
                            <th>Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {orders
                            .slice((ordersPage - 1) * ordersItemsPerPage, ordersPage * ordersItemsPerPage)
                            .map((order) => (
                              <tr key={order.id}>
                                <td style={{ fontWeight: 600 }}>{order.order?.reference || order.id.slice(0, 8)}</td>
                                <td>{order.order?.profiles?.full_name || 'Customer'}</td>
                                <td>{order.items.map((item: any) => item.part_name).join(', ')}</td>
                                <td>{order.items[0]?.description || 'N/A'}</td>
                                <td>{order.items.reduce((sum: number, item: any) => sum + item.quantity, 0)}</td>
                                <td style={{ fontWeight: 600 }}>
                                  KES {order.total_amount.toLocaleString()}
                                </td>
                                <td>
                                  <span className={`badge ${
                                    order.status === 'pending' ? 'badge-yellow' :
                                    order.status === 'confirmed' ? 'badge-blue' :
                                    order.status === 'processing' ? 'badge-purple' :
                                    order.status === 'ready' ? 'badge-cyan' :
                                    'badge-green'
                                  }`}>
                                    {order.status === 'pending' ? 'PENDING' :
                                     order.status === 'confirmed' ? 'CONFIRMED' :
                                     order.status === 'processing' ? 'PROCESSING' :
                                     order.status === 'ready' ? 'READY' :
                                     'DELIVERED'}
                                  </span>
                                </td>
                                <td>{new Date(order.created_at).toLocaleDateString()}</td>
                                <td>
                                  {order.status === 'pending' && (
                                    <button
                                      className="btn btn-primary btn-sm"
                                      onClick={() => handleStatusChangeRequest(
                                        order.id,
                                        order.order_id || order.order?.id,
                                        'pending',
                                        'confirmed',
                                        order.reference
                                      )}
                                    >
                                      Confirm
                                    </button>
                                  )}
                                  {order.status === 'confirmed' && (
                                    <button
                                      className="btn btn-primary btn-sm"
                                      onClick={() => handleStatusChangeRequest(
                                        order.id,
                                        order.order_id || order.order?.id,
                                        'confirmed',
                                        'processing',
                                        order.reference
                                      )}
                                    >
                                      Start Processing
                                    </button>
                                  )}
                                  {order.status === 'processing' && (
                                    <button
                                      className="btn btn-primary btn-sm"
                                      onClick={() => handleStatusChangeRequest(
                                        order.id,
                                        order.order_id || order.order?.id,
                                        'processing',
                                        'ready',
                                        order.reference
                                      )}
                                    >
                                      Mark Ready
                                    </button>
                                  )}
                                  {order.status === 'ready' && (
                                    <button
                                      className="btn btn-success btn-sm"
                                      onClick={() => handleStatusChangeRequest(
                                        order.id,
                                        order.order_id || order.order?.id,
                                        'ready',
                                        'delivered',
                                        order.reference
                                      )}
                                    >
                                      Mark Delivered
                                    </button>
                                  )}
                                </td>
                              </tr>
                            ))}
                        </tbody>
                      </table>
                    </div>

                    {/* Pagination */}
                    {orders.length > ordersItemsPerPage && (
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '1.5rem', paddingTop: '1rem', borderTop: '1px solid var(--border)' }}>
                        <div style={{ fontSize: '0.85rem', color: 'var(--muted)' }}>
                          Showing {((ordersPage - 1) * ordersItemsPerPage) + 1} to {Math.min(ordersPage * ordersItemsPerPage, orders.length)} of {orders.length} orders
                        </div>
                        <div style={{ display: 'flex', gap: '0.5rem' }}>
                          <button
                            onClick={() => setOrdersPage(Math.max(1, ordersPage - 1))}
                            disabled={ordersPage === 1}
                            style={{
                              padding: '0.5rem 1rem',
                              background: ordersPage === 1 ? 'var(--bg)' : 'white',
                              border: '1px solid var(--border)',
                              borderRadius: 'var(--radius-sm)',
                              cursor: ordersPage === 1 ? 'not-allowed' : 'pointer',
                              fontWeight: 600,
                              fontSize: '0.85rem',
                              opacity: ordersPage === 1 ? 0.5 : 1
                            }}
                          >
                            Previous
                          </button>
                          <div style={{ display: 'flex', gap: '0.25rem', alignItems: 'center' }}>
                            {Array.from({ length: Math.ceil(orders.length / ordersItemsPerPage) }, (_, i) => i + 1).slice(0, 5).map((page) => (
                              <button
                                key={page}
                                onClick={() => setOrdersPage(page)}
                                style={{
                                  padding: '0.5rem 0.75rem',
                                  background: ordersPage === page ? 'var(--red)' : 'white',
                                  border: ordersPage === page ? '1px solid var(--red)' : '1px solid var(--border)',
                                  borderRadius: 'var(--radius-sm)',
                                  cursor: 'pointer',
                                  fontWeight: 600,
                                  fontSize: '0.85rem',
                                  color: ordersPage === page ? 'white' : 'var(--text)'
                                }}
                              >
                                {page}
                              </button>
                            ))}
                            {Math.ceil(orders.length / ordersItemsPerPage) > 5 && (
                              <span style={{ padding: '0.5rem 0.75rem', color: 'var(--muted)' }}>...</span>
                            )}
                          </div>
                          <button
                            onClick={() => setOrdersPage(Math.min(Math.ceil(orders.length / ordersItemsPerPage), ordersPage + 1))}
                            disabled={ordersPage === Math.ceil(orders.length / ordersItemsPerPage)}
                            style={{
                              padding: '0.5rem 1rem',
                              background: ordersPage === Math.ceil(orders.length / ordersItemsPerPage) ? 'var(--bg)' : 'white',
                              border: '1px solid var(--border)',
                              borderRadius: 'var(--radius-sm)',
                              cursor: ordersPage === Math.ceil(orders.length / ordersItemsPerPage) ? 'not-allowed' : 'pointer',
                              fontWeight: 600,
                              fontSize: '0.85rem',
                              opacity: ordersPage === Math.ceil(orders.length / ordersItemsPerPage) ? 0.5 : 1
                            }}
                          >
                            Next
                          </button>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            ) : (
              <div className="card" style={{ padding: '3rem', textAlign: 'center' }}>
                <AlertCircle size={64} style={{ color: '#f59e0b', marginBottom: '1.5rem' }} />
                <h2 style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--text)', marginBottom: '1rem' }}>
                  Account Pending Approval
                </h2>
                <p style={{ fontSize: '1rem', color: 'var(--text-secondary)', marginBottom: '1.5rem', lineHeight: 1.6 }}>
                  Your account is currently under review. Once approved by our team, you will be able to manage your inventory, view orders, and access all features.
                </p>
              </div>
            )}
          </div>
        )}

        {tab === 'history' && (
          <div className="dashboard-content">
            {console.log('=== Rendering History tab ===', profile?.verification_status)}
            {profile?.verification_status === 'verified' ? (
              <div className="card">
                <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem' }}>
                  <div>
                    <h2>Order History</h2>
                    <p style={{ fontSize: '0.85rem', color: 'var(--muted)', marginTop: '0.25rem' }}>
                      {historyOrders.length} delivered order{historyOrders.length !== 1 ? 's' : ''}
                    </p>
                  </div>
                  <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                    <div style={{ position: 'relative' }}>
                      <Search
                        size={16}
                        style={{
                          position: 'absolute',
                          left: '0.75rem',
                          top: '50%',
                          transform: 'translateY(-50%)',
                          color: 'var(--muted)'
                        }}
                      />
                      <input
                        type="text"
                        placeholder="Search orders..."
                        value={historySearch}
                        onChange={(e) => setHistorySearch(e.target.value)}
                        style={{
                          padding: '0.5rem 1rem 0.5rem 2.25rem',
                          border: '1px solid var(--border)',
                          borderRadius: 'var(--radius-sm)',
                          fontSize: '0.85rem',
                          minWidth: '200px',
                          outline: 'none'
                        }}
                        onFocus={(e) => e.currentTarget.style.borderColor = 'var(--red)'}
                        onBlur={(e) => e.currentTarget.style.borderColor = 'var(--border)'}
                      />
                    </div>
                    <button
                      onClick={loadHistory}
                      style={{
                        padding: '0.5rem 1rem',
                        background: 'white',
                        border: '1px solid var(--border)',
                        borderRadius: 'var(--radius-sm)',
                        cursor: 'pointer',
                        fontWeight: 600,
                        fontSize: '0.85rem',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem'
                      }}
                    >
                      <Activity size={16} />
                      Refresh
                    </button>
                  </div>
                </div>
                {loadingHistory ? (
                  <div style={{ padding: '3rem', textAlign: 'center' }}>
                    <div style={{
                      width: '48px',
                      height: '48px',
                      border: '4px solid var(--border)',
                      borderTopColor: 'var(--red)',
                      borderRadius: '50%',
                      margin: '0 auto 1rem',
                      animation: 'spin 1s linear infinite'
                    }} />
                    <p>Loading history...</p>
                  </div>
                ) : historyOrders.length === 0 ? (
                  <div className="empty-state">
                    <Clock size={48} style={{ color: '#9ca3af' }} />
                    <p>No order history yet</p>
                  </div>
                ) : (
                  <>
                    <div className="data-table-wrapper">
                      <table className="data-table">
                        <thead>
                          <tr>
                            <th>Reference</th>
                            <th>Customer</th>
                            <th>Items</th>
                            <th>Description</th>
                            <th>Quantity</th>
                            <th>Price</th>
                            <th>Total</th>
                            <th>Status</th>
                            <th>Date</th>
                            <th>Delivered Date</th>
                          </tr>
                        </thead>
                        <tbody>
                          {historyOrders
                            .filter(order => {
                              if (!historySearch) return true
                              const searchLower = historySearch.toLowerCase()
                              return (
                                order.reference?.toLowerCase().includes(searchLower) ||
                                order.profiles?.full_name?.toLowerCase().includes(searchLower) ||
                                order.profiles?.phone?.includes(searchLower) ||
                                order.items?.some((item: any) => item.part_name?.toLowerCase().includes(searchLower))
                              )
                            })
                            .slice((historyPage - 1) * historyItemsPerPage, historyPage * historyItemsPerPage)
                            .map((order) => (
                              <tr key={order.id}>
                                <td style={{ fontWeight: 600 }}>{order.reference || order.id.slice(0, 8)}</td>
                                <td>{order.profiles?.full_name || 'Customer'}</td>
                                <td>{order.items.map((item: any) => item.part_name).join(', ')}</td>
                                <td>{order.items[0]?.description || 'N/A'}</td>
                                <td>{order.items.reduce((sum: number, item: any) => sum + item.quantity, 0)}</td>
                                <td>KES {order.items[0]?.unit_price?.toLocaleString() || '0'}</td>
                                <td style={{ fontWeight: 600 }}>
                                  KES {order.total_amount.toLocaleString()}
                                </td>
                                <td>
                                  <span className="badge badge-green">DELIVERED</span>
                                </td>
                                <td>{new Date(order.created_at).toLocaleDateString()}</td>
                                <td>{order.completed_at ? new Date(order.completed_at).toLocaleDateString() : 'N/A'}</td>
                              </tr>
                            ))}
                        </tbody>
                      </table>
                    </div>

                    {/* Pagination */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '1.5rem', paddingTop: '1rem', borderTop: '1px solid var(--border)' }}>
                      <div style={{ fontSize: '0.85rem', color: 'var(--muted)' }}>
                        Showing {((historyPage - 1) * historyItemsPerPage) + 1} to {Math.min(historyPage * historyItemsPerPage, historyOrders.length)} of {historyOrders.length} orders
                      </div>
                      <div style={{ display: 'flex', gap: '0.5rem' }}>
                        <button
                          onClick={() => setHistoryPage(Math.max(1, historyPage - 1))}
                          disabled={historyPage === 1}
                          style={{
                            padding: '0.5rem 1rem',
                            background: historyPage === 1 ? 'var(--bg)' : 'white',
                            border: '1px solid var(--border)',
                            borderRadius: 'var(--radius-sm)',
                            cursor: historyPage === 1 ? 'not-allowed' : 'pointer',
                            fontWeight: 600,
                            fontSize: '0.85rem',
                            opacity: historyPage === 1 ? 0.5 : 1
                          }}
                        >
                          Previous
                        </button>
                        <div style={{ display: 'flex', gap: '0.25rem', alignItems: 'center' }}>
                          {Array.from({ length: Math.ceil(historyOrders.length / historyItemsPerPage) }, (_, i) => i + 1).slice(0, 5).map((page) => (
                            <button
                              key={page}
                              onClick={() => setHistoryPage(page)}
                              style={{
                                padding: '0.5rem 0.75rem',
                                background: historyPage === page ? 'var(--red)' : 'white',
                                border: historyPage === page ? '1px solid var(--red)' : '1px solid var(--border)',
                                borderRadius: 'var(--radius-sm)',
                                cursor: 'pointer',
                                fontWeight: 600,
                                fontSize: '0.85rem',
                                color: historyPage === page ? 'white' : 'var(--text)'
                              }}
                            >
                              {page}
                            </button>
                          ))}
                          {Math.ceil(historyOrders.length / historyItemsPerPage) > 5 && (
                            <span style={{ padding: '0.5rem 0.75rem', color: 'var(--muted)' }}>...</span>
                          )}
                        </div>
                        <button
                          onClick={() => setHistoryPage(Math.min(Math.ceil(historyOrders.length / historyItemsPerPage), historyPage + 1))}
                          disabled={historyPage === Math.ceil(historyOrders.length / historyItemsPerPage)}
                          style={{
                            padding: '0.5rem 1rem',
                            background: historyPage === Math.ceil(historyOrders.length / historyItemsPerPage) ? 'var(--bg)' : 'white',
                            border: '1px solid var(--border)',
                            borderRadius: 'var(--radius-sm)',
                            cursor: historyPage === Math.ceil(historyOrders.length / historyItemsPerPage) ? 'not-allowed' : 'pointer',
                            fontWeight: 600,
                            fontSize: '0.85rem',
                            opacity: historyPage === Math.ceil(historyOrders.length / historyItemsPerPage) ? 0.5 : 1
                          }}
                        >
                          Next
                        </button>
                      </div>
                    </div>
                  </>
                )}
              </div>
            ) : (
              <div className="card" style={{ padding: '3rem', textAlign: 'center' }}>
                <AlertCircle size={64} style={{ color: '#f59e0b', marginBottom: '1.5rem' }} />
                <h2 style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--text)', marginBottom: '1rem' }}>
                  Account Pending Approval
                </h2>
                <p style={{ fontSize: '1rem', color: 'var(--text-secondary)', marginBottom: '1.5rem', lineHeight: 1.6 }}>
                  Your account is currently under review. Once approved by our team, you will be able to manage your inventory, view orders, and access all features.
                </p>
              </div>
            )}
          </div>
        )}

        {tab === 'earnings' && (
          <div className="dashboard-content">
            {profile?.verification_status === 'verified' ? (
              <div className="card">
                <div className="card-header">
                  <h2>Earnings</h2>
                </div>
                <div className="empty-state">
                  <DollarSign size={48} style={{ color: '#9ca3af' }} />
                  <p>No earnings yet</p>
                </div>
              </div>
            ) : (
              <div className="card" style={{ padding: '3rem', textAlign: 'center' }}>
                <AlertCircle size={64} style={{ color: '#f59e0b', marginBottom: '1.5rem' }} />
                <h2 style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--text)', marginBottom: '1rem' }}>
                  Account Pending Approval
                </h2>
                <p style={{ fontSize: '1rem', color: 'var(--text-secondary)', marginBottom: '1.5rem', lineHeight: 1.6 }}>
                  Your account is currently under review. Once approved by our team, you will be able to manage your inventory, view orders, and access all features.
                </p>
              </div>
            )}
          </div>
        )}

        {tab === 'reports' && (
          <div className="dashboard-content">
            {profile?.verification_status === 'verified' ? (
              <div className="card">
                <h2 className="card-title" style={{ fontSize: '1.25rem', color: 'var(--text)', marginBottom: '1.5rem' }}>Sales Reports</h2>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1.5rem', marginBottom: '2rem' }}>
                  <div style={{ padding: '1.5rem', background: 'var(--bg)', borderRadius: 'var(--radius)', border: '2px solid var(--border)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem' }}>
                      <Package size={24} style={{ color: 'var(--red)' }} />
                      <div>
                        <div style={{ fontSize: '0.875rem', color: 'var(--muted)', fontWeight: 600 }}>Total Parts</div>
                        <div style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--text)' }}>{totalParts}</div>
                      </div>
                    </div>
                  </div>
                  <div style={{ padding: '1.5rem', background: 'var(--bg)', borderRadius: 'var(--radius)', border: '2px solid var(--border)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem' }}>
                      <DollarSign size={24} style={{ color: 'var(--green)' }} />
                      <div>
                        <div style={{ fontSize: '0.875rem', color: 'var(--muted)', fontWeight: 600 }}>Total Revenue</div>
                        <div style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--text)' }}>KES 0</div>
                      </div>
                    </div>
                  </div>
                  <div style={{ padding: '1.5rem', background: 'var(--bg)', borderRadius: 'var(--radius)', border: '2px solid var(--border)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem' }}>
                      <TrendingUp size={24} style={{ color: 'var(--blue)' }} />
                      <div>
                        <div style={{ fontSize: '0.875rem', color: 'var(--muted)', fontWeight: 600 }}>Low Stock</div>
                        <div style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--text)' }}>{lowStockParts}</div>
                      </div>
                    </div>
                  </div>
                </div>
                <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--muted)' }}>
                  <BarChart3 size={48} style={{ margin: '0 auto 1rem', opacity: 0.5 }} />
                  <div style={{ fontSize: '1rem', fontWeight: 500 }}>More reports coming soon</div>
                  <div style={{ fontSize: '0.875rem', marginTop: '0.5rem' }}>Detailed sales analytics and charts will be available here</div>
                </div>
              </div>
            ) : (
              <div className="card" style={{ padding: '3rem', textAlign: 'center' }}>
                <AlertCircle size={64} style={{ color: '#f59e0b', marginBottom: '1.5rem' }} />
                <h2 style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--text)', marginBottom: '1rem' }}>
                  Account Pending Approval
                </h2>
                <p style={{ fontSize: '1rem', color: 'var(--text-secondary)', marginBottom: '1.5rem', lineHeight: 1.6 }}>
                  Your account is currently under review. Once approved by our team, you will be able to manage your inventory, view orders, and access all features.
                </p>
              </div>
            )}
          </div>
        )}

        {tab === 'team' && (
          <div className="dashboard-content">
            {profile?.verification_status === 'verified' ? (
              <div className="card" style={{ marginBottom: '2rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                    <div style={{
                      width: '48px',
                      height: '48px',
                      borderRadius: '12px',
                      background: 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <Users size={24} color="white" />
                    </div>
                    <div>
                      <h2 className="card-title" style={{ fontSize: '1.5rem', color: 'var(--text)', marginBottom: '0.25rem' }}>Team Members</h2>
                      <p style={{ color: 'var(--text-secondary)', fontSize: '0.875rem', margin: 0 }}>
                        Manage staff members who can access your dashboard
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => setShowAddTeamMemberModal(true)}
                    style={{
                      padding: '0.75rem 1.5rem',
                      background: 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)',
                      border: 'none',
                      borderRadius: '8px',
                      color: 'white',
                      fontSize: '0.875rem',
                      fontWeight: 600,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      transition: 'all 0.2s',
                      boxShadow: '0 2px 4px rgba(220, 38, 38, 0.2)'
                    }}
                    onMouseOver={(e) => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 4px 6px rgba(220, 38, 38, 0.3)' }}
                    onMouseOut={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 2px 4px rgba(220, 38, 38, 0.2)' }}
                  >
                    <Plus size={18} />
                    Add Team Member
                  </button>
                </div>

                {teamMembers.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--text-secondary)' }}>
                    <Users size={48} style={{ margin: '0 auto 1rem', opacity: 0.5 }} />
                    <p>No team members yet</p>
                    <p style={{ fontSize: '0.875rem' }}>Add staff members to help manage your operations</p>
                  </div>
                ) : (
                  <div style={{ display: 'grid', gap: '1rem', marginTop: '1.5rem' }}>
                    {teamMembers.map((member) => (
                      <div
                        key={member.id}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          padding: '1rem',
                          background: 'white',
                          borderRadius: '12px',
                          border: '1px solid var(--border)',
                          boxShadow: '0 1px 3px rgba(0,0,0,0.08)'
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                          <div style={{
                            width: '40px',
                            height: '40px',
                            borderRadius: '50%',
                            background: 'var(--bg)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '1.25rem',
                            fontWeight: 700,
                            color: 'var(--text-secondary)'
                          }}>
                            {member.profiles?.full_name?.charAt(0).toUpperCase() || 'U'}
                          </div>
                          <div>
                            <div style={{ fontWeight: 600, color: 'var(--text)' }}>
                              {member.profiles?.full_name || 'Unknown'}
                            </div>
                            <div style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                              {member.profiles?.email || 'No email'}
                            </div>
                          </div>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                          <span style={{
                            padding: '0.25rem 0.75rem',
                            borderRadius: '20px',
                            fontSize: '0.75rem',
                            fontWeight: 600,
                            background: member.role === 'admin' ? '#fee2e2' : 'var(--bg)',
                            color: member.role === 'admin' ? '#dc2626' : 'var(--text-secondary)'
                          }}>
                            {member.role === 'admin' ? 'Admin' : 'Staff'}
                          </span>
                          <button
                            onClick={() => removeTeamMember(member.user_id)}
                            style={{
                              padding: '0.5rem 0.75rem',
                              borderRadius: '8px',
                              background: 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)',
                              border: 'none',
                              color: 'white',
                              fontSize: '0.875rem',
                              fontWeight: 600,
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '0.5rem',
                              transition: 'all 0.2s',
                              boxShadow: '0 2px 4px rgba(220, 38, 38, 0.2)'
                            }}
                            onMouseOver={(e) => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 4px 6px rgba(220, 38, 38, 0.3)' }}
                            onMouseOut={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 2px 4px rgba(220, 38, 38, 0.2)' }}
                          >
                            <Trash2 size={14} />
                            Remove
                          </button>
                          <button
                            onClick={() => resendInvitation(member)}
                            style={{
                              padding: '0.5rem 0.75rem',
                              borderRadius: '8px',
                              background: 'white',
                              border: '2px solid var(--border)',
                              color: 'var(--text)',
                              fontSize: '0.875rem',
                              fontWeight: 600,
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '0.5rem',
                              transition: 'all 0.2s'
                            }}
                            onMouseOver={(e) => { e.currentTarget.style.borderColor = '#dc2626'; e.currentTarget.style.color = '#dc2626' }}
                            onMouseOut={(e) => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.color = 'var(--text)' }}
                          >
                            <Mail size={14} />
                            Resend
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <div className="card">
                <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--text-secondary)' }}>
                  <Users size={48} style={{ margin: '0 auto 1rem', opacity: 0.5 }} />
                  <p>Account Pending Approval</p>
                  <p style={{ fontSize: '0.875rem' }}>You need to be verified to manage team members</p>
                </div>
              </div>
            )}
          </div>
        )}

        {tab === 'settings' && (
          <div className="dashboard-content">
            {/* Business Information Card */}
            <div className="card" style={{ padding: '1.5rem', marginBottom: '1.5rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.5rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                  <div style={{
                    width: '48px',
                    height: '48px',
                    borderRadius: '12px',
                    background: 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <Building2 size={24} color="white" />
                  </div>
                  <div>
                    <h2 className="card-title" style={{ fontSize: '1.25rem', color: 'var(--text)', marginBottom: '0.25rem' }}>Business Information</h2>
                    <p style={{ color: 'var(--text-secondary)', fontSize: '0.875rem', margin: 0 }}>Your business details</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowEditBusinessModal(true)}
                  style={{
                    padding: '0.5rem 1rem',
                    background: 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)',
                    border: 'none',
                    borderRadius: '8px',
                    color: 'white',
                    fontSize: '0.875rem',
                    fontWeight: 600,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    transition: 'all 0.2s',
                    boxShadow: '0 2px 4px rgba(220, 38, 38, 0.2)'
                  }}
                  onMouseOver={(e) => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 4px 6px rgba(220, 38, 38, 0.3)' }}
                  onMouseOut={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 2px 4px rgba(220, 38, 38, 0.2)' }}
                >
                  <Wrench size={16} />
                  Edit
                </button>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1.5rem' }}>
                <div>
                  <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginBottom: '0.25rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Business Name</div>
                  <div style={{ fontSize: '1rem', fontWeight: 600, color: 'var(--text)' }}>{profile?.business_name || 'N/A'}</div>
                </div>
                <div>
                  <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginBottom: '0.25rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Business Registration</div>
                  <div style={{ fontSize: '1rem', fontWeight: 600, color: 'var(--text)' }}>{profile?.business_registration || 'N/A'}</div>
                </div>
                <div>
                  <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginBottom: '0.25rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Tax PIN</div>
                  <div style={{ fontSize: '1rem', fontWeight: 600, color: 'var(--text)' }}>{profile?.tax_pin || 'N/A'}</div>
                </div>
                <div>
                  <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginBottom: '0.25rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Business Address</div>
                  <div style={{ fontSize: '1rem', fontWeight: 600, color: 'var(--text)' }}>{profile?.business_address || 'N/A'}</div>
                </div>
                <div>
                  <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginBottom: '0.25rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Business Phone</div>
                  <div style={{ fontSize: '1rem', fontWeight: 600, color: 'var(--text)' }}>{profile?.business_phone || 'N/A'}</div>
                </div>
                <div>
                  <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginBottom: '0.25rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Business Email</div>
                  <div style={{ fontSize: '1rem', fontWeight: 600, color: 'var(--text)' }}>{profile?.business_email || 'N/A'}</div>
                </div>
              </div>
            </div>

            {/* Banking & Payment Details Card */}
            <div className="card" style={{ padding: '1.5rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.5rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                  <div style={{
                    width: '48px',
                    height: '48px',
                    borderRadius: '12px',
                    background: 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <CreditCard size={24} color="white" />
                  </div>
                  <div>
                    <h2 className="card-title" style={{ fontSize: '1.25rem', color: 'var(--text)', marginBottom: '0.25rem' }}>Banking & Payment</h2>
                    <p style={{ color: 'var(--text-secondary)', fontSize: '0.875rem', margin: 0 }}>Your payment information</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowEditBankingModal(true)}
                  style={{
                    padding: '0.5rem 1rem',
                    background: 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)',
                    border: 'none',
                    borderRadius: '8px',
                    color: 'white',
                    fontSize: '0.875rem',
                    fontWeight: 600,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    transition: 'all 0.2s',
                    boxShadow: '0 2px 4px rgba(220, 38, 38, 0.2)'
                  }}
                  onMouseOver={(e) => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 4px 6px rgba(220, 38, 38, 0.3)' }}
                  onMouseOut={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 2px 4px rgba(220, 38, 38, 0.2)' }}
                >
                  <Wrench size={16} />
                  Edit
                </button>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1.5rem' }}>
                <div>
                  <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginBottom: '0.25rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Bank Name</div>
                  <div style={{ fontSize: '1rem', fontWeight: 600, color: 'var(--text)' }}>{profile?.bank_name || 'N/A'}</div>
                </div>
                <div>
                  <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginBottom: '0.25rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Bank Account Number</div>
                  <div style={{ fontSize: '1rem', fontWeight: 600, color: 'var(--text)' }}>{profile?.bank_account_number || 'N/A'}</div>
                </div>
                <div>
                  <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginBottom: '0.25rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>M-Pesa Number</div>
                  <div style={{ fontSize: '1rem', fontWeight: 600, color: 'var(--text)' }}>{profile?.mpesa_number || 'N/A'}</div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Edit Business Information Modal */}
        {showEditBusinessModal && (
          <div style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}>
            <div style={{
              background: 'white',
              borderRadius: 'var(--radius-lg)',
              padding: '2rem',
              maxWidth: '600px',
              width: '90%',
              maxHeight: '90vh',
              overflow: 'auto'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                <h2 style={{ fontSize: '1.25rem', fontWeight: 700, color: 'var(--text)' }}>Edit Business Information</h2>
                <button
                  onClick={() => setShowEditBusinessModal(false)}
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: '0.5rem'
                  }}
                >
                  <XIcon size={20} />
                </button>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                <div>
                  <label style={{ display: 'block', fontWeight: 600, marginBottom: '0.5rem', fontSize: '0.875rem' }}>Business Name</label>
                  <input
                    type="text"
                    value={settings.business_name}
                    onChange={(e) => setSettings({ ...settings, business_name: e.target.value })}
                    placeholder="Enter your business name"
                    style={{ width: '100%', padding: '0.75rem 1rem', border: '2px solid var(--border)', borderRadius: 'var(--radius-sm)' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontWeight: 600, marginBottom: '0.5rem', fontSize: '0.875rem' }}>Business Registration Number</label>
                  <input
                    type="text"
                    value={settings.business_registration}
                    onChange={(e) => setSettings({ ...settings, business_registration: e.target.value })}
                    placeholder="e.g., BN/2023/123456"
                    style={{ width: '100%', padding: '0.75rem 1rem', border: '2px solid var(--border)', borderRadius: 'var(--radius-sm)' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontWeight: 600, marginBottom: '0.5rem', fontSize: '0.875rem' }}>Tax PIN</label>
                  <input
                    type="text"
                    value={settings.tax_pin}
                    onChange={(e) => setSettings({ ...settings, tax_pin: e.target.value })}
                    placeholder="e.g., A001234567P"
                    style={{ width: '100%', padding: '0.75rem 1rem', border: '2px solid var(--border)', borderRadius: 'var(--radius-sm)' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontWeight: 600, marginBottom: '0.5rem', fontSize: '0.875rem' }}>Business Phone</label>
                  <input
                    type="tel"
                    value={settings.business_phone}
                    onChange={(e) => handlePhoneChange(e.target.value, 'business_phone')}
                    onKeyPress={handlePhoneKeyPress}
                    placeholder="e.g., +2547XXXXXXXXX"
                    style={{ width: '100%', padding: '0.75rem 1rem', border: '2px solid var(--border)', borderRadius: 'var(--radius-sm)' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontWeight: 600, marginBottom: '0.5rem', fontSize: '0.875rem' }}>Business Email</label>
                  <input
                    type="email"
                    value={settings.business_email}
                    onChange={(e) => setSettings({ ...settings, business_email: e.target.value })}
                    placeholder="e.g., parts@autoparts.co.ke"
                    style={{ width: '100%', padding: '0.75rem 1rem', border: '2px solid var(--border)', borderRadius: 'var(--radius-sm)' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontWeight: 600, marginBottom: '0.5rem', fontSize: '0.875rem' }}>Business Address</label>
                  <input
                    type="text"
                    value={settings.business_address}
                    onChange={(e) => setSettings({ ...settings, business_address: e.target.value })}
                    placeholder="e.g., Industrial Area, Nairobi"
                    style={{ width: '100%', padding: '0.75rem 1rem', border: '2px solid var(--border)', borderRadius: 'var(--radius-sm)' }}
                  />
                </div>
                <div style={{ display: 'flex', gap: '1rem', marginTop: '1rem' }}>
                  <button
                    onClick={() => setShowEditBusinessModal(false)}
                    style={{
                      flex: 1,
                      padding: '0.75rem 1rem',
                      background: 'white',
                      border: '2px solid var(--border)',
                      borderRadius: 'var(--radius-sm)',
                      color: 'var(--text)',
                      fontSize: '0.875rem',
                      fontWeight: 600,
                      cursor: 'pointer'
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={async () => {
                      await handleSaveSettings()
                      setShowEditBusinessModal(false)
                      setSuccessMessage('Business information updated successfully!')
                      setShowSuccessModal(true)
                      setTimeout(() => setShowSuccessModal(false), 1500)
                    }}
                    disabled={saving}
                    style={{
                      flex: 1,
                      padding: '0.75rem 1rem',
                      background: saving ? '#9ca3af' : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      border: 'none',
                      borderRadius: 'var(--radius-sm)',
                      color: 'white',
                      fontSize: '0.875rem',
                      fontWeight: 600,
                      cursor: saving ? 'not-allowed' : 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '0.5rem'
                    }}
                  >
                    {saving ? 'Saving...' : <><Save size={16} /> Save Changes</>}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Add Team Member Modal */}
        {showAddTeamMemberModal && (
          <div style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}>
            <div style={{
              background: 'white',
              borderRadius: '16px',
              padding: '2rem',
              maxWidth: '500px',
              width: '90%',
              maxHeight: '90vh',
              overflowY: 'auto'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.5rem' }}>
                <h2 style={{ fontSize: '1.25rem', fontWeight: 700, color: 'var(--text)', margin: 0 }}>Add Team Member</h2>
                <button
                  onClick={() => setShowAddTeamMemberModal(false)}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0.5rem' }}
                >
                  <X size={24} style={{ color: 'var(--text-secondary)' }} />
                </button>
              </div>

              <div style={{ display: 'grid', gap: '1rem' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 600, color: 'var(--text)', marginBottom: '0.5rem' }}>
                    Full Name *
                  </label>
                  <input
                    type="text"
                    value={newTeamMember.full_name}
                    onChange={(e) => setNewTeamMember({ ...newTeamMember, full_name: e.target.value })}
                    placeholder="John Doe"
                    style={{
                      width: '100%',
                      padding: '0.875rem',
                      border: '2px solid var(--border)',
                      borderRadius: '0.75rem',
                      fontSize: '1rem'
                    }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 600, color: 'var(--text)', marginBottom: '0.5rem' }}>
                    Email *
                  </label>
                  <input
                    type="email"
                    value={newTeamMember.email}
                    onChange={(e) => setNewTeamMember({ ...newTeamMember, email: e.target.value })}
                    placeholder="john@example.com"
                    style={{
                      width: '100%',
                      padding: '0.875rem',
                      border: '2px solid var(--border)',
                      borderRadius: '0.75rem',
                      fontSize: '1rem'
                    }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 600, color: 'var(--text)', marginBottom: '0.5rem' }}>
                    Role *
                  </label>
                  <select
                    value={newTeamMember.role}
                    onChange={(e) => setNewTeamMember({ ...newTeamMember, role: e.target.value as 'admin' | 'staff' })}
                    style={{
                      width: '100%',
                      padding: '0.875rem',
                      border: '2px solid var(--border)',
                      borderRadius: '0.75rem',
                      fontSize: '1rem',
                      background: 'white'
                    }}
                  >
                    <option value="staff">Staff - Full access to dashboard</option>
                    <option value="admin">Admin - Full access + can manage team</option>
                  </select>
                </div>

                <div style={{
                  padding: '1rem',
                  background: 'var(--bg)',
                  borderRadius: '8px',
                  fontSize: '0.875rem',
                  color: 'var(--text-secondary)'
                }}>
                  <strong>Note:</strong> The team member will be created with a strong temporary password (12+ characters with uppercase, lowercase, numbers, and special characters). They should change it after first login.
                </div>

                <div style={{ display: 'flex', gap: '0.75rem', marginTop: '2rem' }}>
                  <button
                    onClick={() => {
                      setShowAddTeamMemberModal(false)
                      setNewTeamMember({ email: '', full_name: '', role: 'staff' })
                    }}
                    style={{
                      flex: 1,
                      padding: '0.75rem 1rem',
                      background: 'white',
                      border: '2px solid var(--border)',
                      borderRadius: 'var(--radius-sm)',
                      color: 'var(--text)',
                      fontSize: '0.875rem',
                      fontWeight: 600,
                      cursor: 'pointer'
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={addTeamMember}
                    disabled={addingTeamMember || !newTeamMember.email || !newTeamMember.full_name}
                    style={{
                      flex: 1,
                      padding: '0.75rem 1rem',
                      background: 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)',
                      border: 'none',
                      borderRadius: 'var(--radius-sm)',
                      color: 'white',
                      fontSize: '0.875rem',
                      fontWeight: 600,
                      cursor: addingTeamMember ? 'not-allowed' : 'pointer',
                      opacity: addingTeamMember ? 0.6 : 1
                    }}
                  >
                    {addingTeamMember ? 'Adding...' : 'Add Team Member'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Edit Banking Modal */}
        {showEditBankingModal && (
          <div style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}>
            <div style={{
              background: 'white',
              borderRadius: 'var(--radius-lg)',
              padding: '2rem',
              maxWidth: '500px',
              width: '90%',
              maxHeight: '90vh',
              overflow: 'auto'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                <h2 style={{ fontSize: '1.25rem', fontWeight: 700, color: 'var(--text)' }}>Edit Banking Information</h2>
                <button
                  onClick={() => setShowEditBankingModal(false)}
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: '0.5rem'
                  }}
                >
                  <XIcon size={20} />
                </button>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                <div>
                  <label style={{ display: 'block', fontWeight: 600, marginBottom: '0.5rem', fontSize: '0.875rem' }}>Bank Name</label>
                  <input
                    type="text"
                    value={settings.bank_name}
                    onChange={(e) => setSettings({ ...settings, bank_name: e.target.value })}
                    placeholder="e.g., Equity Bank"
                    style={{ width: '100%', padding: '0.75rem 1rem', border: '2px solid var(--border)', borderRadius: 'var(--radius-sm)' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontWeight: 600, marginBottom: '0.5rem', fontSize: '0.875rem' }}>Bank Account Number</label>
                  <input
                    type="text"
                    value={settings.bank_account_number}
                    onChange={(e) => setSettings({ ...settings, bank_account_number: e.target.value })}
                    placeholder="e.g., 1234567890"
                    style={{ width: '100%', padding: '0.75rem 1rem', border: '2px solid var(--border)', borderRadius: 'var(--radius-sm)' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontWeight: 600, marginBottom: '0.5rem', fontSize: '0.875rem' }}>M-Pesa Number</label>
                  <input
                    type="tel"
                    value={settings.mpesa_number}
                    onChange={(e) => handlePhoneChange(e.target.value, 'mpesa_number')}
                    onKeyPress={handlePhoneKeyPress}
                    placeholder="e.g., 07XXXXXXXXX"
                    style={{ width: '100%', padding: '0.75rem 1rem', border: '2px solid var(--border)', borderRadius: 'var(--radius-sm)' }}
                  />
                </div>
                <div style={{ display: 'flex', gap: '1rem', marginTop: '1rem' }}>
                  <button
                    onClick={() => setShowEditBankingModal(false)}
                    style={{
                      flex: 1,
                      padding: '0.75rem 1rem',
                      background: 'white',
                      border: '2px solid var(--border)',
                      borderRadius: 'var(--radius-sm)',
                      color: 'var(--text)',
                      fontSize: '0.875rem',
                      fontWeight: 600,
                      cursor: 'pointer'
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={async () => {
                      await handleSaveSettings()
                      setShowEditBankingModal(false)
                      setSuccessMessage('Banking information updated successfully!')
                      setShowSuccessModal(true)
                      setTimeout(() => setShowSuccessModal(false), 1500)
                    }}
                    disabled={saving}
                    style={{
                      flex: 1,
                      padding: '0.75rem 1rem',
                      background: saving ? '#9ca3af' : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      border: 'none',
                      borderRadius: 'var(--radius-sm)',
                      color: 'white',
                      fontSize: '0.875rem',
                      fontWeight: 600,
                      cursor: saving ? 'not-allowed' : 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '0.5rem'
                    }}
                  >
                    {saving ? 'Saving...' : <><Save size={16} /> Save Changes</>}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Success Modal - Rendered first for highest z-index */}
        {showSuccessModal && (
          <div style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            animation: 'fadeIn 0.3s ease'
          }}>
            <div style={{
              background: 'white',
              borderRadius: 'var(--radius-lg)',
              padding: '3rem',
              maxWidth: '400px',
              width: '90%',
              textAlign: 'center',
              boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
            }}>
              <div style={{
                width: '80px',
                height: '80px',
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 1.5rem',
                boxShadow: '0 4px 6px rgba(220, 38, 38, 0.3)'
              }}>
                <CheckCircle size={40} color="white" />
              </div>
              <h2 style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--text)', marginBottom: '0.5rem' }}>
                Success!
              </h2>
              <p style={{ fontSize: '1rem', color: 'var(--text-secondary)', marginBottom: '1.5rem' }}>
                {successMessage}
              </p>
              <button
                onClick={() => setShowSuccessModal(false)}
                style={{
                  padding: '0.75rem 2rem',
                  background: 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)',
                  color: 'white',
                  border: 'none',
                  borderRadius: 'var(--radius-sm)',
                  fontSize: '0.875rem',
                  fontWeight: 600,
                  cursor: 'pointer',
                  boxShadow: '0 4px 6px rgba(220, 38, 38, 0.3)',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px)'
                  e.currentTarget.style.boxShadow = '0 6px 8px rgba(220, 38, 38, 0.4)'
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)'
                  e.currentTarget.style.boxShadow = '0 4px 6px rgba(220, 38, 38, 0.3)'
                }}
              >
                Done
              </button>
            </div>
          </div>
        )}

        {/* Error Modal */}
        {showErrorModal && (
          <div style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1001,
            animation: 'fadeIn 0.3s ease'
          }}>
            <div style={{
              background: 'white',
              borderRadius: 'var(--radius-lg)',
              padding: '3rem',
              maxWidth: '400px',
              width: '90%',
              textAlign: 'center'
            }}>
              <div style={{
                width: '80px',
                height: '80px',
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 1.5rem'
              }}>
                <XCircle size={40} color="white" />
              </div>
              <h2 style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--text)', marginBottom: '0.5rem' }}>
                Error
              </h2>
              <p style={{ fontSize: '1rem', color: 'var(--text-secondary)', marginBottom: '1.5rem' }}>
                {errorMessage}
              </p>
              <button
                onClick={() => setShowErrorModal(false)}
                style={{
                  padding: '0.75rem 2rem',
                  background: 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)',
                  color: 'white',
                  border: 'none',
                  borderRadius: 'var(--radius-sm)',
                  fontSize: '0.875rem',
                  fontWeight: 600,
                  cursor: 'pointer'
                }}
              >
                Close
              </button>
            </div>
          </div>
        )}

        {/* Add Part Modal */}
        {showAddPartModal && (
          <div style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}>
            <div style={{
              background: 'white',
              borderRadius: 'var(--radius-lg)',
              padding: '2rem',
              maxWidth: '500px',
              width: '90%',
              maxHeight: '90vh',
              overflow: 'auto'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                <h2 style={{ fontSize: '1.25rem', fontWeight: 700, color: 'var(--text)' }}>Add New Part</h2>
                <button
                  onClick={() => {
                    setShowAddPartModal(false)
                    setSelectedImage(null)
                    setImagePreview(null)
                    setNewPart({
                      name: '',
                      category: '',
                      price: '',
                      stock: '',
                      condition: 'new',
                      description: '',
                      car_make: '',
                      car_model: '',
                      year_range: ''
                    })
                  }}
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: '0.5rem'
                  }}
                >
                  <X size={20} />
                </button>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                <div>
                  <label style={{ display: 'block', fontWeight: 600, marginBottom: '0.5rem', fontSize: '0.875rem' }}>Part Image</label>
                  <div style={{ 
                    border: '2px dashed var(--border)', 
                    borderRadius: 'var(--radius-sm)', 
                    padding: '2rem', 
                    textAlign: 'center',
                    cursor: 'pointer',
                    position: 'relative',
                    background: imagePreview ? 'white' : 'var(--bg)'
                  }}>
                    {imagePreview ? (
                      <div>
                        <img 
                          src={imagePreview} 
                          alt="Preview" 
                          style={{ 
                            maxWidth: '100%', 
                            maxHeight: '200px', 
                            objectFit: 'contain',
                            borderRadius: 'var(--radius-sm)'
                          }} 
                        />
                        <button
                          onClick={(e) => { e.stopPropagation(); setSelectedImage(null); setImagePreview(null) }}
                          style={{
                            marginTop: '0.5rem',
                            padding: '0.5rem 1rem',
                            background: '#fee2e2',
                            color: '#dc2626',
                            border: 'none',
                            borderRadius: 'var(--radius-sm)',
                            cursor: 'pointer',
                            fontWeight: 600,
                            fontSize: '0.875rem'
                          }}
                        >
                          Remove Image
                        </button>
                      </div>
                    ) : (
                      <div>
                        <input
                          type="file"
                          accept="image/*"
                          onChange={(e) => {
                            const file = e.target.files?.[0]
                            if (file) {
                              setSelectedImage(file)
                              const reader = new FileReader()
                              reader.onload = (e) => setImagePreview(e.target?.result as string)
                              reader.readAsDataURL(file)
                            }
                          }}
                          style={{ display: 'none' }}
                          id="part-image-input"
                        />
                        <label htmlFor="part-image-input" style={{ cursor: 'pointer' }}>
                          <div style={{ fontSize: '3rem', marginBottom: '0.5rem' }}>📷</div>
                          <div style={{ fontSize: '0.875rem', color: 'var(--muted)' }}>
                            Click to upload image
                          </div>
                          <div style={{ fontSize: '0.75rem', color: 'var(--muted)', marginTop: '0.25rem' }}>
                            PNG, JPG up to 5MB
                          </div>
                        </label>
                      </div>
                    )}
                  </div>
                </div>
                <div>
                  <label style={{ display: 'block', fontWeight: 600, marginBottom: '0.5rem', fontSize: '0.875rem' }}>Part Name</label>
                  <input
                    type="text"
                    value={newPart.name}
                    onChange={(e) => setNewPart({ ...newPart, name: e.target.value })}
                    placeholder="e.g., Alternator, Brake Pads, Oil Filter"
                    style={{ width: '100%', padding: '0.75rem 1rem', border: '2px solid var(--border)', borderRadius: 'var(--radius-sm)' }}
                  />
                </div>
                
                <div style={{ padding: '1rem', background: '#f8fafc', borderRadius: '8px', border: '1px solid var(--border)' }}>
                  <div style={{ fontWeight: 600, fontSize: '0.9rem', marginBottom: '1rem', color: 'var(--text)' }}>Vehicle Compatibility</div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '0.75rem' }}>
                    <div>
                      <label style={{ display: 'block', fontWeight: 600, marginBottom: '0.5rem', fontSize: '0.8rem', color: 'var(--muted)' }}>Car Make</label>
                      <input
                        type="text"
                        value={newPart.car_make}
                        onChange={(e) => setNewPart({ ...newPart, car_make: e.target.value })}
                        placeholder="e.g., Toyota, Nissan, Honda"
                        style={{ width: '100%', padding: '0.6rem 0.75rem', border: '2px solid var(--border)', borderRadius: 'var(--radius-sm)', fontSize: '0.875rem' }}
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', fontWeight: 600, marginBottom: '0.5rem', fontSize: '0.8rem', color: 'var(--muted)' }}>Car Model</label>
                      <input
                        type="text"
                        value={newPart.car_model}
                        onChange={(e) => setNewPart({ ...newPart, car_model: e.target.value })}
                        placeholder="e.g., Corolla, Camry, Xterra"
                        style={{ width: '100%', padding: '0.6rem 0.75rem', border: '2px solid var(--border)', borderRadius: 'var(--radius-sm)', fontSize: '0.875rem' }}
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', fontWeight: 600, marginBottom: '0.5rem', fontSize: '0.8rem', color: 'var(--muted)' }}>Year Range</label>
                      <input
                        type="text"
                        value={newPart.year_range}
                        onChange={(e) => setNewPart({ ...newPart, year_range: e.target.value })}
                        placeholder="e.g., 2015-2020"
                        style={{ width: '100%', padding: '0.6rem 0.75rem', border: '2px solid var(--border)', borderRadius: 'var(--radius-sm)', fontSize: '0.875rem' }}
                      />
                    </div>
                  </div>
                </div>

                <div>
                  <label style={{ display: 'block', fontWeight: 600, marginBottom: '0.5rem', fontSize: '0.875rem' }}>Category</label>
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <select
                      value={newPart.category}
                      onChange={(e) => setNewPart({ ...newPart, category: e.target.value })}
                      style={{ flex: 1, padding: '0.75rem 1rem', border: '2px solid var(--border)', borderRadius: 'var(--radius-sm)' }}
                    >
                      <option value="">Select category</option>
                      {categories.map(cat => (
                        <option key={cat.id} value={cat.name}>{cat.name}</option>
                      ))}
                    </select>
                    <button
                      onClick={() => setShowAddCategoryModal(true)}
                      style={{
                        padding: '0.75rem 1rem',
                        background: 'var(--red)',
                        color: 'white',
                        border: 'none',
                        borderRadius: 'var(--radius-sm)',
                        cursor: 'pointer',
                        fontWeight: 600,
                        fontSize: '0.875rem'
                      }}
                    >
                      +
                    </button>
                  </div>
                </div>
                <div>
                  <label style={{ display: 'block', fontWeight: 600, marginBottom: '0.5rem', fontSize: '0.875rem' }}>Price (KES)</label>
                  <input
                    type="number"
                    value={newPart.price}
                    onChange={(e) => setNewPart({ ...newPart, price: e.target.value })}
                    placeholder="Enter price"
                    style={{ width: '100%', padding: '0.75rem 1rem', border: '2px solid var(--border)', borderRadius: 'var(--radius-sm)' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontWeight: 600, marginBottom: '0.5rem', fontSize: '0.875rem' }}>Stock Quantity</label>
                  <input
                    type="number"
                    value={newPart.stock}
                    onChange={(e) => setNewPart({ ...newPart, stock: e.target.value })}
                    placeholder="Enter stock quantity"
                    style={{ width: '100%', padding: '0.75rem 1rem', border: '2px solid var(--border)', borderRadius: 'var(--radius-sm)' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontWeight: 600, marginBottom: '0.5rem', fontSize: '0.875rem' }}>Condition</label>
                  <select
                    value={newPart.condition}
                    onChange={(e) => setNewPart({ ...newPart, condition: e.target.value as 'new' | 'used' | 'refurbished' })}
                    style={{ width: '100%', padding: '0.75rem 1rem', border: '2px solid var(--border)', borderRadius: 'var(--radius-sm)' }}
                  >
                    <option value="new">New</option>
                    <option value="used">Used</option>
                    <option value="refurbished">Refurbished</option>
                  </select>
                </div>
                <div>
                  <label style={{ display: 'block', fontWeight: 600, marginBottom: '0.5rem', fontSize: '0.875rem' }}>Description</label>
                  <textarea
                    value={newPart.description}
                    onChange={(e) => setNewPart({ ...newPart, description: e.target.value })}
                    placeholder="Enter part description, OEM number, compatibility notes..."
                    rows={3}
                    style={{ width: '100%', padding: '0.75rem 1rem', border: '2px solid var(--border)', borderRadius: 'var(--radius-sm)', resize: 'vertical' }}
                  />
                </div>
                <button
                  className="btn-primary"
                  onClick={async () => {
                    if (!profile) return
                    console.log('Adding part for vendor:', profile.id, profile.business_name)
                    setUploadingImage(true)
                    try {
                      let imageUrl = ''
                      if (selectedImage) {
                        const uploadResult = await uploadImage(selectedImage, 'parts-inventory')
                        imageUrl = uploadResult.secure_url
                      }

                      // Save part to database
                      const partData = {
                        vendor_id: profile.id,
                        name: newPart.name,
                        description: newPart.description,
                        category: newPart.category,
                        brand: '',
                        condition: newPart.condition,
                        price: Number(newPart.price),
                        stock: Number(newPart.stock),
                        image_url: imageUrl,
                        location: profile.business_address || '',
                        compatible_makes: newPart.car_make,
                        compatible_models: newPart.car_model,
                        car_make: newPart.car_make,
                        car_model: newPart.car_model,
                        year_range: newPart.year_range,
                        is_active: true
                      }
                      console.log('Inserting part data:', partData)
                      const { error, data: insertedData } = await supabase.from('parts').insert(partData).select()
                      console.log('Insert result:', { error, insertedData })

                      if (error) {
                        console.error('Error adding part:', error)
                        setErrorMessage('Failed to add part. Please try again.')
                        setShowErrorModal(true)
                      } else {
                        console.log('Part added successfully to database')
                        // Refresh parts list
                        const { data } = await supabase.from('parts').select('*').eq('vendor_id', profile.id)
                        console.log('Refreshed parts:', data)
                        setParts((data as Part[]) || [])
                        setShowAddPartModal(false)
                        setSelectedImage(null)
                        setImagePreview(null)
                        setNewPart({
                          name: '',
                          category: '',
                          price: '',
                          stock: '',
                          condition: 'new',
                          description: '',
                          car_make: '',
                          car_model: '',
                          year_range: ''
                        })
                        setSuccessMessage('Part added successfully!')
                        setShowSuccessModal(true)
                        setTimeout(() => setShowSuccessModal(false), 1500)
                      }
                    } catch (error) {
                      console.error('Error adding part:', error)
                      setErrorMessage('Failed to add part. Please try again.')
                      setShowErrorModal(true)
                    } finally {
                      setUploadingImage(false)
                    }
                  }}
                  disabled={uploadingImage}
                  style={{ marginTop: '1rem' }}
                >
                  {uploadingImage ? 'Adding...' : 'Add Part'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Edit Part Modal */}
        {showEditPartModal && (
          <div style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}>
            <div style={{
              background: 'white',
              borderRadius: 'var(--radius-lg)',
              padding: '2rem',
              maxWidth: '500px',
              width: '90%',
              maxHeight: '90vh',
              overflow: 'auto'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                <h2 style={{ fontSize: '1.25rem', fontWeight: 700, color: 'var(--text)' }}>Edit Part</h2>
                <button
                  onClick={() => {
                    setShowEditPartModal(false)
                    setEditingPart(null)
                    setSelectedImage(null)
                    setImagePreview(null)
                  }}
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: '0.5rem'
                  }}
                >
                  <X size={20} />
                </button>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                <div>
                  <label style={{ display: 'block', fontWeight: 600, marginBottom: '0.5rem', fontSize: '0.875rem' }}>Part Image</label>
                  <div style={{ 
                    border: '2px dashed var(--border)', 
                    borderRadius: 'var(--radius-sm)', 
                    padding: '2rem', 
                    textAlign: 'center',
                    cursor: 'pointer',
                    position: 'relative',
                    background: imagePreview ? 'white' : 'var(--bg)'
                  }}>
                    {imagePreview ? (
                      <div>
                        <img 
                          src={imagePreview} 
                          alt="Preview" 
                          style={{ 
                            maxWidth: '100%', 
                            maxHeight: '200px', 
                            objectFit: 'contain',
                            borderRadius: 'var(--radius-sm)'
                          }} 
                        />
                        <button
                          onClick={(e) => { e.stopPropagation(); setSelectedImage(null); setImagePreview(null) }}
                          style={{
                            marginTop: '0.5rem',
                            padding: '0.5rem 1rem',
                            background: '#fee2e2',
                            color: '#dc2626',
                            border: 'none',
                            borderRadius: 'var(--radius-sm)',
                            cursor: 'pointer',
                            fontWeight: 600,
                            fontSize: '0.875rem'
                          }}
                        >
                          Remove Image
                        </button>
                      </div>
                    ) : (
                      <div>
                        <input
                          type="file"
                          accept="image/*"
                          onChange={(e) => {
                            const file = e.target.files?.[0]
                            if (file) {
                              setSelectedImage(file)
                              const reader = new FileReader()
                              reader.onloadend = () => setImagePreview(reader.result as string)
                              reader.readAsDataURL(file)
                            }
                          }}
                          style={{ position: 'absolute', inset: 0, opacity: 0, cursor: 'pointer' }}
                        />
                        <Package size={48} style={{ color: 'var(--muted)', marginBottom: '0.5rem' }} />
                        <p style={{ color: 'var(--muted)', fontSize: '0.875rem' }}>Click to upload new image</p>
                      </div>
                    )}
                  </div>
                </div>
                <div>
                  <label style={{ display: 'block', fontWeight: 600, marginBottom: '0.5rem', fontSize: '0.875rem' }}>Part Name</label>
                  <input
                    type="text"
                    value={newPart.name}
                    onChange={(e) => setNewPart({ ...newPart, name: e.target.value })}
                    placeholder="Enter part name"
                    style={{ width: '100%', padding: '0.75rem 1rem', border: '2px solid var(--border)', borderRadius: 'var(--radius-sm)' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontWeight: 600, marginBottom: '0.5rem', fontSize: '0.875rem' }}>Category</label>
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <select
                      value={newPart.category}
                      onChange={(e) => setNewPart({ ...newPart, category: e.target.value })}
                      style={{ flex: 1, padding: '0.75rem 1rem', border: '2px solid var(--border)', borderRadius: 'var(--radius-sm)' }}
                    >
                      <option value="">Select category</option>
                      {categories.map(cat => (
                        <option key={cat.id} value={cat.name}>{cat.name}</option>
                      ))}
                    </select>
                    <button
                      onClick={() => setShowAddCategoryModal(true)}
                      style={{
                        padding: '0.75rem 1rem',
                        background: 'var(--red)',
                        color: 'white',
                        border: 'none',
                        borderRadius: 'var(--radius-sm)',
                        cursor: 'pointer',
                        fontWeight: 600,
                        fontSize: '0.875rem'
                      }}
                    >
                      +
                    </button>
                  </div>
                </div>
                <div>
                  <label style={{ display: 'block', fontWeight: 600, marginBottom: '0.5rem', fontSize: '0.875rem' }}>Price (KES)</label>
                  <input
                    type="number"
                    value={newPart.price}
                    onChange={(e) => setNewPart({ ...newPart, price: e.target.value })}
                    placeholder="Enter price"
                    style={{ width: '100%', padding: '0.75rem 1rem', border: '2px solid var(--border)', borderRadius: 'var(--radius-sm)' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontWeight: 600, marginBottom: '0.5rem', fontSize: '0.875rem' }}>Stock Quantity</label>
                  <input
                    type="number"
                    value={newPart.stock}
                    onChange={(e) => setNewPart({ ...newPart, stock: e.target.value })}
                    placeholder="Enter stock quantity"
                    style={{ width: '100%', padding: '0.75rem 1rem', border: '2px solid var(--border)', borderRadius: 'var(--radius-sm)' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontWeight: 600, marginBottom: '0.5rem', fontSize: '0.875rem' }}>Condition</label>
                  <select
                    value={newPart.condition}
                    onChange={(e) => setNewPart({ ...newPart, condition: e.target.value as 'new' | 'used' | 'refurbished' })}
                    style={{ width: '100%', padding: '0.75rem 1rem', border: '2px solid var(--border)', borderRadius: 'var(--radius-sm)' }}
                  >
                    <option value="new">New</option>
                    <option value="used">Used</option>
                    <option value="refurbished">Refurbished</option>
                  </select>
                </div>
                <div>
                  <label style={{ display: 'block', fontWeight: 600, marginBottom: '0.5rem', fontSize: '0.875rem' }}>Description</label>
                  <textarea
                    value={newPart.description}
                    onChange={(e) => setNewPart({ ...newPart, description: e.target.value })}
                    placeholder="Enter part description, OEM number, compatibility notes..."
                    rows={3}
                    style={{ width: '100%', padding: '0.75rem 1rem', border: '2px solid var(--border)', borderRadius: 'var(--radius-sm)', resize: 'vertical' }}
                  />
                </div>
                <button
                  className="btn-primary"
                  onClick={handleSaveEditPart}
                  disabled={uploadingImage}
                  style={{ marginTop: '1rem' }}
                >
                  {uploadingImage ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Add Category Modal */}
        {showAddCategoryModal && (
          <div style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}>
            <div style={{
              background: 'white',
              borderRadius: 'var(--radius-lg)',
              padding: '2rem',
              maxWidth: '400px',
              width: '90%'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                <h2 style={{ fontSize: '1.25rem', fontWeight: 700, color: 'var(--text)' }}>Add Category</h2>
                <button
                  onClick={() => setShowAddCategoryModal(false)}
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: '0.5rem'
                  }}
                >
                  <X size={20} />
                </button>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                <div>
                  <label style={{ display: 'block', fontWeight: 600, marginBottom: '0.5rem', fontSize: '0.875rem' }}>Category Name</label>
                  <input
                    type="text"
                    value={newCategoryName}
                    onChange={(e) => setNewCategoryName(e.target.value)}
                    placeholder="Enter category name"
                    style={{ width: '100%', padding: '0.75rem 1rem', border: '2px solid var(--border)', borderRadius: 'var(--radius-sm)' }}
                  />
                </div>
                <div style={{ display: 'flex', gap: '1rem', marginTop: '1rem' }}>
                  <button
                    onClick={() => setShowAddCategoryModal(false)}
                    style={{
                      flex: 1,
                      padding: '0.75rem 1rem',
                      background: 'white',
                      border: '2px solid var(--border)',
                      borderRadius: 'var(--radius-sm)',
                      color: 'var(--text)',
                      fontSize: '0.875rem',
                      fontWeight: 600,
                      cursor: 'pointer'
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    className="btn-primary"
                    onClick={() => {
                      // Add category logic here
                      setShowAddCategoryModal(false)
                    }}
                    style={{
                      flex: 1,
                      padding: '0.75rem 1rem',
                      background: 'var(--red)',
                      color: 'white',
                      border: 'none',
                      borderRadius: 'var(--radius-sm)',
                      cursor: 'pointer',
                      fontWeight: 600,
                      fontSize: '0.875rem'
                    }}
                  >
                    Add Category
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </main>

      {/* Team Success Modal */}
      <TeamMemberSuccessModal
        isOpen={showTeamSuccessModal}
        onClose={() => setShowTeamSuccessModal(false)}
        memberData={teamSuccessModalData}
        emailSent={teamSuccessModalData.emailSent}
      />

      {/* Team Error Modal */}
      <ErrorModal
        isOpen={showTeamErrorModal}
        onClose={() => setShowTeamErrorModal(false)}
        title={teamErrorModalData.title}
        message={teamErrorModalData.message}
      />

      {/* Confirm Modal */}
      <ConfirmModal
        isOpen={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        onConfirm={confirmModalData.onConfirm}
        title={confirmModalData.title}
        message={confirmModalData.message}
        confirmText="Remove"
        cancelText="Cancel"
      />

      {/* Switch Role Modal */}
      {showSwitchRoleModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'white',
            borderRadius: '0.75rem',
            padding: '1.5rem',
            maxWidth: '400px',
            width: '90%'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
              <h2 style={{ fontSize: '1.1rem', fontWeight: 600, color: 'var(--text)' }}>
                Switch Role
              </h2>
              <button
                onClick={() => setShowSwitchRoleModal(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '0.25rem'
                }}
              >
                <X size={20} />
              </button>
            </div>

            <div style={{ display: 'grid', gap: '0.5rem', marginBottom: '1rem' }}>
              {userRoles.map((role) => {
                const config = availableRoles.find(r => r.value === role)
                const Icon = config?.icon || Package
                const isCurrent = role === 'parts_vendor'
                return (
                  <button
                    key={role}
                    onClick={() => {
                      if (role !== 'parts_vendor') {
                        localStorage.setItem('preferredRole', role)
                        window.location.reload()
                      }
                    }}
                    disabled={isCurrent}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.75rem',
                      padding: '0.75rem',
                      borderRadius: '0.375rem',
                      border: isCurrent ? '2px solid var(--red)' : '1px solid var(--border)',
                      background: isCurrent ? 'rgba(239, 68, 68, 0.05)' : 'white',
                      cursor: isCurrent ? 'not-allowed' : 'pointer',
                      opacity: isCurrent ? 0.7 : 1,
                      transition: 'all 0.2s',
                      textAlign: 'left'
                    }}
                  >
                    <Icon size={18} style={{ color: isCurrent ? 'var(--red)' : 'var(--text)' }} />
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: '0.875rem', fontWeight: 600, color: 'var(--text)' }}>
                        {role.charAt(0).toUpperCase() + role.slice(1)}
                      </div>
                    </div>
                    {isCurrent && (
                      <span style={{
                        padding: '0.125rem 0.375rem',
                        background: 'var(--red)',
                        color: 'white',
                        borderRadius: '0.25rem',
                        fontSize: '0.7rem',
                        fontWeight: 500
                      }}>
                        Current
                      </span>
                    )}
                  </button>
                )
              })}
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowSwitchRoleModal(false)}
                style={{
                  padding: '0.5rem 1rem',
                  borderRadius: '0.375rem',
                  border: '1px solid var(--border)',
                  background: 'white',
                  color: 'var(--text)',
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Order Status Change Confirmation Modal */}
      {showOrderStatusConfirm && orderStatusConfirmData && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'white',
            borderRadius: '0.75rem',
            padding: '1.5rem',
            maxWidth: '400px',
            width: '90%'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
              <h2 style={{ fontSize: '1.1rem', fontWeight: 600, color: 'var(--text)' }}>
                Update Order Status
              </h2>
              <button
                onClick={() => setShowOrderStatusConfirm(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '0.25rem'
                }}
              >
                <X size={20} />
              </button>
            </div>

            <p style={{ fontSize: '0.9rem', color: 'var(--text)', marginBottom: '1.5rem', lineHeight: 1.5 }}>
              Are you sure you want to change order <strong>{orderStatusConfirmData.orderRef}</strong> from <strong>{orderStatusConfirmData.currentStatus.toUpperCase()}</strong> to <strong>{orderStatusConfirmData.newStatus.toUpperCase()}</strong>?
            </p>

            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowOrderStatusConfirm(false)}
                style={{
                  padding: '0.5rem 1rem',
                  borderRadius: '0.375rem',
                  border: '1px solid var(--border)',
                  background: 'white',
                  color: 'var(--text)',
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={confirmStatusChange}
                style={{
                  padding: '0.5rem 1rem',
                  borderRadius: '0.375rem',
                  border: 'none',
                  background: 'var(--red)',
                  color: 'white',
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  cursor: 'pointer'
                }}
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Order Status Change Success Modal */}
      {showOrderStatusSuccess && orderStatusSuccessData && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'white',
            borderRadius: '0.75rem',
            padding: '2rem',
            maxWidth: '400px',
            width: '90%',
            textAlign: 'center'
          }}>
            <div style={{
              width: '64px',
              height: '64px',
              borderRadius: '50%',
              background: 'var(--green-light)',
              color: 'var(--green)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 1rem'
            }}>
              <CheckCircle size={32} />
            </div>
            <h2 style={{ fontSize: '1.25rem', fontWeight: 700, color: 'var(--text)', marginBottom: '0.5rem' }}>
              Order Status Updated
            </h2>
            <p style={{ fontSize: '0.9rem', color: 'var(--text-secondary)', marginBottom: '1.5rem', lineHeight: 1.5 }}>
              Order <strong>{orderStatusSuccessData.orderRef}</strong> has been updated to <strong>{orderStatusSuccessData.newStatus.toUpperCase()}</strong>
            </p>
            <button
              onClick={() => setShowOrderStatusSuccess(false)}
              style={{
                padding: '0.5rem 1rem',
                borderRadius: '0.375rem',
                border: 'none',
                background: 'var(--red)',
                color: 'white',
                fontSize: '0.875rem',
                fontWeight: 500,
                cursor: 'pointer'
              }}
            >
              OK
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
